<template>
  <BaseModal
    class="order-modal"
    :class="{
      'is-quote': isQuote,
      'is-editing-products': editing === 'productsBox'
    }"
    :value="isOpenModal"
    @input="changeModalStatus"
    :fullPage="inline"
    :allowClose="!inline"
    :clickOutsideClose="!inline"
  >
    <BaseProductModal
      v-model="showProductModal"
      @closeProduct="openedProduct = ''"
      :code="openedProduct"
    />
    <BaseSyncModal
      v-if="orderFull"
      v-model="showSyncModal"
      @close="showSyncModal = false"
      :orderStatus="orderFull.status"
      :orderNumber="orderFull.orderNumber"
    />
    <UnpaidOrderCommentModal
      :show="showUnpaidCommentModal"
      @close="showUnpaidCommentModal = false"
      @submit="handleUnpaidComment"
    />
    <AfterConfirmedOrder
      :show="showAfterConfirmedModal"
      :orderNumber="orderFull ? orderFull.orderNumber : ''"
      :orderTotal="orderFull ? orderFull.total : 0"
      :customerEmail="orderFull ? orderFull.customerEmail : ''"
      :pendingPayment="orderFull ? orderFull.pendingPayment : 0"
      :creditRetail="orderFull ? orderFull.creditRetail : ''"
      @close="showAfterConfirmedModal = false"
      @email-sent="handleEmailSent"
      @payment-added="handlePaymentAdded"
      @continue-as-credit="handleContinueAsCredit"
      @show-email-modal="showEmailModal = true"
      @show-stripe-modal="handleShowStripeModal"
      @skip="handleAfterConfirmSkip"
      @complete="handleAfterConfirmComplete"
    />
    <!-- Fuera del menu de opciones: antes vivian dentro y se destruian al
         cerrarlo (y "show-email-modal" no abria nada con el menu cerrado) -->
    <SMSOrderModal
      v-if="showSMSModal && orderFull"
      @close="showSMSModal = false"
      :orderNumber="orderFull.orderNumber"
      :customerPhone="orderPhone"
    />
    <EmailOrderModal
      v-if="showEmailModal && orderFull"
      @close="showEmailModal = false"
      :orderNumber="orderFull.orderNumber"
      :email="orderFull.customer?.generalInfo?.email || orderFull.customerEmail"
      :infoautoId="orderFull.customerInfoauto?.generalInfo?.infoautoId || ''"
      :infoautoEmail="orderFull.customerInfoauto?.generalInfo?.email || ''"
      :infoautoName="orderFull.customerInfoauto?.generalInfo?.name || ''"
    />
    <template v-slot:header>
      <div
        v-if="orderFull || orderUUID"
        class="order-header"
        :class="{ 'is-compact': headerScrolled }"
      >
        <div class="order-header-main">
          <h2 class="order-number">
            {{
              isQuote
                ? $t("message.order.quote_label")
                : $t("message.order.order_label")
            }}
            <span>{{ headerReference }}</span>
          </h2>
          <template v-if="isQuote && orderFull">
            <input
              v-if="editingQuoteName"
              ref="quoteNameInput"
              v-model="quoteNameInput"
              class="quote-name-input"
              maxlength="120"
              :placeholder="$t('message.order.quote_name_placeholder')"
              @keyup.enter="saveQuoteName"
              @keyup.esc.stop="editingQuoteName = false"
              @blur="saveQuoteName"
            />
            <button
              v-else
              type="button"
              class="quote-name"
              :disabled="!!orderFull.convertedOrderNumber"
              :class="{ 'is-empty': !orderFull.quoteName }"
              :title="$t('message.order.rename_quote')"
              @click="startQuoteNameEdit"
            >
              {{
                orderFull.quoteName ||
                  $t("message.order.quote_name_placeholder")
              }}
              <i class="mdi mdi-pencil-outline"></i>
            </button>
          </template>
          <span
            v-if="orderFull"
            class="status-badge"
            :class="`tone-${statusTone}`"
            :title="$t('message.order.status_label')"
          >
            <span class="status-dot"></span>
            {{ translatedStatus }}
          </span>
          <span
            v-if="orderFull && orderFull.departamentoNombre"
            class="department-pill"
          >
            <i class="mdi mdi-store-outline"></i>
            {{ orderFull.departamentoNombre }}
          </span>
        </div>
        <div v-if="orderFull && !headerScrolled" class="order-header-meta">
          <span v-if="headerCustomerName" class="meta-item">
            <i class="mdi mdi-account-outline"></i>
            {{ headerCustomerName }}
          </span>
          <span v-if="orderFull.createDate" class="meta-item">
            <i class="mdi mdi-calendar-blank-outline"></i>
            {{ orderFull.createDate | dateWithHours }}
          </span>
          <span class="meta-item">
            {{
              isQuote
                ? $t("message.order.quote_total")
                : $t("message.order.total_order")
            }}
            <strong>{{ orderFull.total | currencyFormatted(true) }}</strong>
          </span>
          <span
            v-if="!isQuote"
            class="meta-item meta-payment"
            :class="hasAmountDue ? 'is-due' : 'is-paid'"
          >
            <template v-if="hasAmountDue">
              {{ $t("message.order.amount_due") }}
              <strong>
                {{ orderFull.pendingPayment | currencyFormatted(true) }}
              </strong>
            </template>
            <template v-else>
              <i class="mdi mdi-check-circle-outline"></i>
              {{ $t("message.order.amount_paid") }}
            </template>
          </span>
        </div>
      </div>
    </template>
    <div
      class="order-view relative"
      v-if="isOpenModal && orderFull && !loading"
      :key="renderId"
      ref="orderContent"
    >
      <!-- Relacionadas + acciones de estado + menu de opciones -->
      <div v-if="editing !== 'productsBox'" class="order-toolbar">
        <div
          v-if="
            orderFull.parentOrderNumber ||
              (orderFull.childOrders && orderFull.childOrders.length)
          "
          class="toolbar-related"
        >
          <template v-if="orderFull.parentOrderNumber">
            <span class="related-label">
              {{ $t("message.order.this_order_is_child_of") }}
            </span>
            <button
              type="button"
              class="related-chip"
              @click="$emit('openParentOrder', orderFull.parentOrderNumber)"
            >
              <i class="mdi mdi-arrow-top-left"></i>
              #{{ orderFull.parentOrderNumber }}
            </button>
          </template>
          <template
            v-if="orderFull.childOrders && orderFull.childOrders.length"
          >
            <span class="related-label">
              {{ $t("message.order.this_order_has_child_orders") }}
            </span>
            <button
              v-for="childOrder in orderFull.childOrders"
              :key="`child-${childOrder}`"
              type="button"
              class="related-chip"
              @click="$emit('openChildOrder', childOrder)"
            >
              <i class="mdi mdi-subdirectory-arrow-right"></i>
              #{{ childOrder }}
            </button>
          </template>
        </div>

        <div class="toolbar-actions">
          <button
            v-if="orderFull.vin && userCanUseAIFeatures"
            type="button"
            class="ob-btn ob-btn-ai"
            :disabled="verifyingVin"
            @click="verifyVin"
          >
            <i
              class="mdi"
              :class="verifyingVin ? 'mdi-loading mdi-spin' : 'mdi-creation'"
            ></i>
            {{ verifyingVin ? "Verifying..." : "Verify VIN" }}
          </button>
          <button
            v-if="isQuote && orderFull.convertedOrderNumber"
            type="button"
            class="ob-btn ob-btn-success"
            @click="$emit('quoteConverted', orderFull.convertedOrderNumber)"
          >
            <i class="mdi mdi-check-circle-outline"></i>
            {{
              $t("message.order.quote_view_order", {
                order: orderFull.convertedOrderNumber
              })
            }}
          </button>
          <button
            v-if="isQuote && !orderFull.convertedOrderNumber"
            type="button"
            class="ob-btn"
            :class="{ 'ob-btn-success': orderFull.readyAt }"
            :disabled="markingQuoteReady"
            :title="
              $t('message.order.quote_ready_help', {
                days: orderFull.quotePriceValidDays
              })
            "
            @click="markQuoteReady(true)"
          >
            <i
              class="mdi"
              :class="
                markingQuoteReady
                  ? 'mdi-loading mdi-spin'
                  : orderFull.readyAt
                  ? 'mdi-account-check-outline'
                  : 'mdi-send-check-outline'
              "
            ></i>
            <template v-if="orderFull.readyAt">
              {{
                $t("message.order.quote_ready_until", {
                  date: $options.filters.dateWithHours(
                    orderFull.pricesValidUntil
                  )
                })
              }}
            </template>
            <template v-else>
              {{ $t("message.order.mark_quote_ready") }}
            </template>
          </button>
          <button
            v-if="isQuote && !orderFull.convertedOrderNumber"
            type="button"
            class="ob-btn ob-btn-primary"
            :disabled="convertingQuote"
            @click="convertQuote"
          >
            <i
              class="mdi"
              :class="
                convertingQuote
                  ? 'mdi-loading mdi-spin'
                  : 'mdi-cart-arrow-right'
              "
            ></i>
            {{ $t("message.order.convert_to_order") }}
          </button>
          <button
            v-if="
              !isQuote && orderFull.status === 'Draft' && !orderCanBeSplitted
            "
            type="button"
            class="ob-btn ob-btn-primary"
            @click="confirmDraftOrder()"
          >
            <i class="mdi mdi-check-circle-outline"></i>
            {{ $t("message.order.confirm_order") }}
          </button>
          <button
            v-if="
              !isQuote && orderFull.status === 'Draft' && orderCanBeSplitted
            "
            type="button"
            class="ob-btn ob-btn-primary"
            @click="confirmDraftOrder(true)"
          >
            <i class="mdi mdi-call-split"></i>
            {{ $t("message.order.confirm_and_split_order") }}
          </button>
          <button
            v-if="
              !isQuote && orderFull.status === 'Draft' && orderCanBeSplitted
            "
            type="button"
            class="ob-btn"
            @click="confirmDraftOrder(false)"
          >
            {{ $t("message.order.confirm_order_without_split") }}
          </button>
          <button
            v-if="orderFull.availableNextStatus.includes('Awaiting Shipment')"
            type="button"
            class="ob-btn ob-btn-primary"
            @click="moveToAwaitingShipment()"
          >
            <i class="mdi mdi-package-variant-closed"></i>
            {{ $t("message.order.move_to_awaiting_shipment") }}
          </button>
          <button
            v-if="orderFull.availableNextStatus.includes('VIN verification')"
            type="button"
            class="ob-btn"
            @click="updateOrderStatus('VIN verification')"
          >
            <i class="mdi mdi-car-info"></i>
            {{ $t("message.order.move_to_vin_verification") }}
          </button>
          <button
            v-if="
              (orderFull.availableNextStatus.includes('Awaiting Payment') ||
                orderFull.statusPayment === '0') &&
                !isQuote &&
                orderFull.status != 'Draft' &&
                orderFull.status != 'Awaiting Payment'
            "
            type="button"
            class="ob-btn"
            @click="updateOrderStatus('Awaiting Payment')"
          >
            <i class="mdi mdi-cash"></i>
            {{ $t("message.order.move_to_awaiting_payment") }}
          </button>
          <button
            v-if="orderFull.availableNextStatus.includes('Shipped')"
            type="button"
            class="ob-btn ob-btn-success"
            @click="updateOrderStatus('Shipped')"
          >
            <i class="mdi mdi-truck-check-outline"></i>
            {{ $t("message.order.mark_order_shipped") }}
          </button>
          <button
            v-if="orderFull.availableNextStatus.includes('Dropship')"
            type="button"
            class="ob-btn"
            @click="updateOrderStatus('Dropship')"
          >
            <i class="mdi mdi-truck-fast-outline"></i>
            {{ $t("message.order.mark_order_dropship") }}
          </button>
          <button
            v-if="
              orderFull.availableNextStatus.includes('warehouse_allocation')
            "
            type="button"
            class="ob-btn"
            @click="sendToWarehouseAllocation()"
          >
            <i class="mdi mdi-warehouse"></i>
            {{ $t("message.order.send_to_warehouse_allocation") }}
          </button>
          <button
            v-if="orderFull.availableNextStatus.includes('On Hold')"
            type="button"
            class="ob-btn ob-btn-danger"
            @click="updateOrderStatus('On Hold')"
          >
            <i class="mdi mdi-pause-circle-outline"></i>
            {{ $t("message.order.set_order_on_hold") }}
          </button>
          <button
            v-if="
              orderFull.status == 'Dropship' &&
                orderFull.dropshipProcessed == '0'
            "
            type="button"
            class="ob-btn ob-btn-success"
            @click="setProcessedDropship()"
          >
            <i class="mdi mdi-check-all"></i>
            {{ $t("message.order.set_dropship_processed") }}
          </button>
          <button
            v-else-if="
              orderFull.status == 'Dropship' &&
                orderFull.dropshipProcessed == '1'
            "
            type="button"
            class="ob-btn ob-btn-danger"
            @click="setNoProcessedDropship()"
          >
            <i class="mdi mdi-undo"></i>
            {{ $t("message.order.set_dropship_not_processed") }}
          </button>

          <div
            ref="moreActions"
            class="more-actions"
            @keydown.esc="handleOptionsEscape"
          >
            <button
              type="button"
              class="ob-btn"
              :class="{ 'is-open': showOrderOptions }"
              aria-haspopup="menu"
              :aria-expanded="showOrderOptions ? 'true' : 'false'"
              :aria-label="$t('message.order.options')"
              @click="toggleOrderOptions"
            >
              <BaseMiniSpinner v-if="exportingInfoauto" class="btn-spinner" />
              <i v-else class="mdi mdi-dots-horizontal"></i>
              <span class="more-actions-label">
                {{ $t("message.order.options") }}
              </span>
              <i class="mdi mdi-chevron-down chevron"></i>
            </button>

            <div
              v-if="showOrderOptions"
              id="options-box"
              class="actions-menu"
              role="menu"
            >
              <div class="menu-group">
                <div class="menu-group-title">
                  {{ $t("message.order.menu_documents") }}
                </div>
                <button
                  type="button"
                  class="menu-item"
                  @click="
                    closeOrderOptions();
                    showCustomInvoiceModal = true;
                  "
                >
                  <i class="mdi mdi-printer"></i>
                  {{
                    isQuote
                      ? $t("message.order.print_quote")
                      : $t("message.order.print_custom_invoice")
                  }}
                </button>
                <button
                  type="button"
                  class="menu-item"
                  @click="
                    closeOrderOptions();
                    showEmailModal = true;
                  "
                >
                  <i class="mdi mdi-email-outline"></i>
                  {{
                    isQuote
                      ? $t("message.order.send_quote_by_email")
                      : $t("message.order.send_order_by_email")
                  }}
                </button>
                <button
                  type="button"
                  class="menu-item"
                  @click="
                    closeOrderOptions();
                    showSMSModal = true;
                  "
                >
                  <i class="mdi mdi-message-text-outline"></i>
                  <template v-if="orderFull.status != 'Draft'">
                    {{ $t("message.order.send_order_by_sms") }}
                  </template>
                  <template v-else>
                    {{ $t("message.order.send_quote_by_sms") }}
                  </template>
                </button>
                <button
                  v-if="!isQuote"
                  type="button"
                  class="menu-item"
                  @click="
                    closeOrderOptions();
                    showAfterConfirmedModal = true;
                  "
                >
                  <i class="mdi mdi-clipboard-check-outline"></i>
                  {{ $t("message.order.show_after_confirmed_modal") }}
                </button>
              </div>

              <div class="menu-group">
                <div class="menu-group-title">
                  {{
                    isQuote
                      ? $t("message.order.quote_label")
                      : $t("message.order.menu_order")
                  }}
                </div>
                <button
                  v-if="!isQuote"
                  type="button"
                  class="menu-item"
                  @click="
                    closeOrderOptions();
                    createOrderFromCurrent();
                  "
                >
                  <i class="mdi mdi-content-copy"></i>
                  {{ $t("message.order.create_order_from_this") }}
                </button>
                <button
                  v-if="!isQuote"
                  type="button"
                  class="menu-item"
                  @click="
                    closeOrderOptions();
                    splitDropshipOrder();
                  "
                >
                  <i class="mdi mdi-call-split"></i>
                  {{ $t("message.order.split_dropship_order") }}
                </button>
                <button
                  v-if="canBeEdited"
                  type="button"
                  class="menu-item"
                  @click="
                    closeOrderOptions();
                    $nextTick(
                      () => $refs.bulkUpload && $refs.bulkUpload.open()
                    );
                  "
                >
                  <i class="mdi mdi-file-upload-outline"></i>
                  {{ $t("message.order.bulk_add_from_file") }}
                </button>
              </div>

              <div v-if="!isQuote" class="menu-group">
                <div class="menu-group-title">
                  {{ $t("message.order.menu_products") }}
                </div>
                <button
                  type="button"
                  class="menu-item"
                  @click="
                    closeOrderOptions();
                    allocateOrderProducts();
                  "
                >
                  <i class="mdi mdi-warehouse"></i>
                  {{ $t("message.order.allocate_order_products") }}
                </button>
                <button
                  type="button"
                  class="menu-item"
                  @click="
                    closeOrderOptions();
                    verifyOrderProducts();
                  "
                >
                  <i class="mdi mdi-check-all"></i>
                  {{ $t("message.order.verify_order_products") }}
                </button>
                <button
                  type="button"
                  class="menu-item"
                  @click="
                    closeOrderOptions();
                    verifyDropshipProducts();
                  "
                >
                  <i class="mdi mdi-truck-check-outline"></i>
                  {{ $t("message.order.verify_dropship_order") }}
                </button>
              </div>

              <!-- OrderReport abre su propio modal de resultado: el menu no se
                   cierra al usarlo para no destruirlo -->
              <div v-if="!isQuote" class="menu-group">
                <div class="menu-group-title">
                  {{ $t("message.order.menu_reports") }}
                </div>
                <OrderReport :orderNumber="orderFull.orderNumber"></OrderReport>
              </div>

              <div v-if="infoautoExportEnabled && !isQuote" class="menu-group">
                <div class="menu-group-title">
                  {{ $t("message.order.menu_integrations") }}
                </div>
                <button
                  v-if="orderFull.infoautoStatus !== 'migrated'"
                  type="button"
                  class="menu-item"
                  :disabled="exportingInfoauto"
                  @click="
                    closeOrderOptions();
                    sendToInfoauto();
                  "
                >
                  <i class="mdi mdi-cloud-upload-outline"></i>
                  {{ $t("message.order.send_to_infoauto") }}
                </button>
                <div v-else class="menu-item is-static">
                  <i class="mdi mdi-check-circle-outline"></i>
                  {{ $t("message.order.order_already_sent_infoauto") }}
                </div>
              </div>

              <div
                v-if="isQuote && !orderFull.convertedOrderNumber"
                class="menu-group"
              >
                <button
                  v-if="orderFull.readyAt"
                  type="button"
                  class="menu-item"
                  @click="
                    closeOrderOptions();
                    markQuoteReady(false);
                  "
                >
                  <i class="mdi mdi-account-cancel-outline"></i>
                  {{ $t("message.order.unmark_quote_ready") }}
                </button>
                <button
                  type="button"
                  class="menu-item is-danger"
                  @click="
                    closeOrderOptions();
                    removeQuote();
                  "
                >
                  <i class="mdi mdi-delete-outline"></i>
                  {{ $t("message.order.delete_quote") }}
                </button>
              </div>
              <div
                v-if="orderFull.availableNextStatus.includes('Cancelled')"
                class="menu-group"
              >
                <button
                  type="button"
                  class="menu-item is-danger"
                  @click="
                    closeOrderOptions();
                    cancelOrder();
                  "
                >
                  <i class="mdi mdi-cancel"></i>
                  {{ $t("message.order.cancel_order") }}
                </button>
              </div>
              <div v-if="canReactivate" class="menu-group">
                <button
                  type="button"
                  class="menu-item"
                  @click="
                    closeOrderOptions();
                    showReactivateOrderModal = true;
                  "
                >
                  <i class="mdi mdi-restore"></i>
                  {{ $t("message.order.reactivate_order") }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Avisos de la orden -->
      <!-- No se muestran mientras se editan productos: ahi solo estorban -->
      <div
        v-if="
          editing !== 'productsBox' && (errors.length || warningList.length)
        "
        class="order-alerts"
      >
        <div
          v-for="(error, index) in errors"
          :key="`error-${index}`"
          class="order-alert is-error"
        >
          <i class="mdi mdi-alert-circle-outline"></i>
          <span>{{ error }}</span>
        </div>
        <div
          v-for="(warning, index) in warningList"
          :key="`warning-${index}`"
          class="order-alert is-warning"
        >
          <i class="mdi mdi-alert-outline"></i>
          <span>{{ warning }}</span>
        </div>
      </div>

      <!-- VIN Verification Results -->
      <div v-if="vinVerification" class="vin-verification-block">
        <div class="vin-verification-header">
          <span class="vin-verification-title">✦ VIN Verification</span>
          <span class="vin-decoded">
            {{ orderFull.vin }} —
            {{ vinVerification.vinDecoded.make }}
            {{ vinVerification.vinDecoded.model }}
            {{ vinVerification.vinDecoded.year }}
            <span v-if="vinVerification.vinDecoded.engine"
              >· {{ vinVerification.vinDecoded.engine }}</span
            >
          </span>
          <button class="vin-close-btn" @click="vinVerification = null">
            ×
          </button>
        </div>
        <div class="vin-results">
          <div
            v-for="result in vinVerification.results"
            :key="result.nkrCode"
            class="vin-result-row"
            :class="
              result.compatible === true
                ? 'vin-result--ok'
                : result.compatible === false
                ? 'vin-result--fail'
                : 'vin-result--unknown'
            "
          >
            <span class="vin-result-badge">
              {{
                result.compatible === true
                  ? "✓"
                  : result.compatible === false
                  ? "✗"
                  : "?"
              }}
            </span>
            <span class="vin-result-code">{{ result.nkrCode }}</span>
            <span class="vin-result-reason">{{ result.reason }}</span>
          </div>
        </div>
      </div>

      <BulkOrderUpload
        v-if="orderFull"
        ref="bulkUpload"
        :departmentCode="orderFull.departamentoCodigo"
        :priceLevel="orderFull.priceLevel"
        @addProduct="bulkAddProduct"
      />
      <div
        class="order-content w-full"
        :class="{ 'editing-products': editing == 'productsBox' }"
      >
        <!-- Mobile: General / Address / Totals -->
        <div
          v-if="editing !== 'productsBox'"
          class="mobile-section-tabs md:hidden"
          role="tablist"
        >
          <button
            v-for="tab in mobileTabs"
            :key="tab.key"
            type="button"
            role="tab"
            :aria-selected="mobileActiveTab === tab.key ? 'true' : 'false'"
            :class="{ 'is-active': mobileActiveTab === tab.key }"
            @click="mobileActiveTab = tab.key"
          >
            <i class="mdi" :class="tab.icon"></i>
            <span>{{ $t(tab.label) }}</span>
          </button>
        </div>

        <!-- Desktop Layout - Individual Components with Grid Areas -->
        <!-- Escritorio o movil, no ambos: con v-show se montaban las dos copias
             y cada una pedia sus datos (depositos, VINs...) en cada recarga -->
        <GeneralInfo
          v-if="editing !== 'productsBox' && !isMobile"
          class="hidden md:block"
          style="grid-area: a;"
          :order="orderFull"
          @updating="updatingOrder = true"
          @updateFinished="updateFinished"
          @startEditing="updateEditing"
          @finishedEditing="finishedEditing"
          @suggestedForwarder="suggestedForwarder = $event"
          :editing="editing"
          :canBeEdited="canBeEdited"
        />
        <div
          v-if="editing !== 'productsBox' && !isMobile"
          class="hidden md:block"
          style="grid-area: b;"
        >
          <AddressManager
            v-if="orderFull.customerInfoauto || orderFull.status !== 'Draft'"
            :addressSection="addressSection"
            :orderNumber="orderFull.orderNumber"
            :address="getAddressForSection()"
            :customer="orderFull.customer"
            :customerInfoauto="orderFull.customerInfoauto"
            :editing="editing"
            :canBeEdited="canBeEdited"
            :sameShippingAndBilling="orderFull.sameShippingAndBilling == '1'"
            :orderFull="orderFull"
            @updatedSameShippingAndBilling="updatedSameShippingAndBilling"
            @updating="updatingOrder = true"
            @changedSection="changedAddressSection"
            @startEditing="updateEditing"
            @finishedEditing="finishedEditing"
          />
        </div>
        <Totals
          v-if="editing !== 'productsBox' && !isMobile"
          class="hidden md:block"
          style="grid-area: c;"
          :order="orderFull"
          @updating="updatingOrder = true"
          @updateFinished="finishedEditing"
          @startEditing="updateEditing"
          @finishedEditing="finishedEditing"
          :editing="editing"
          :canBeEdited="canBeEdited"
        />

        <!-- Mobile Tabbed Content -->
        <div
          class="block md:hidden"
          v-if="editing !== 'productsBox' && isMobile"
        >
          <!-- General Info Tab -->
          <div
            v-show="mobileActiveTab === 'general'"
            class="mobile-tab-content"
          >
            <GeneralInfo
              :order="orderFull"
              @updating="updatingOrder = true"
              @updateFinished="updateFinished"
              @startEditing="updateEditing"
              @finishedEditing="finishedEditing"
              @suggestedForwarder="suggestedForwarder = $event"
              :editing="editing"
              :canBeEdited="canBeEdited"
            />
          </div>

          <!-- Address Tab -->
          <div
            v-show="mobileActiveTab === 'address'"
            class="mobile-tab-content"
          >
            <AddressManager
              v-if="orderFull.customerInfoauto || orderFull.status !== 'Draft'"
              :addressSection="addressSection"
              :orderNumber="orderFull.orderNumber"
              :address="getAddressForSection()"
              :customer="orderFull.customer"
              :customerInfoauto="orderFull.customerInfoauto"
              :editing="editing"
              :canBeEdited="canBeEdited"
              :sameShippingAndBilling="orderFull.sameShippingAndBilling == '1'"
              :orderFull="orderFull"
              @updatedSameShippingAndBilling="updatedSameShippingAndBilling"
              @updating="updatingOrder = true"
              @changedSection="changedAddressSection"
              @startEditing="updateEditing"
              @finishedEditing="finishedEditing"
            />
          </div>

          <!-- Totals Tab -->
          <div v-show="mobileActiveTab === 'totals'" class="mobile-tab-content">
            <Totals
              :order="orderFull"
              @updating="updatingOrder = true"
              @updateFinished="finishedEditing"
              @startEditing="updateEditing"
              @finishedEditing="finishedEditing"
              :editing="editing"
              :canBeEdited="canBeEdited"
            />
          </div>
        </div>
        <ProductsBox
          v-if="orderFull"
          ref="productsBox"
          :orderNumber="orderFull.orderNumber"
          :products="productsOnly"
          :kitProducts="kitProducts"
          @startEditing="updateEditing"
          @finishedEditing="finishedEditing"
          @viewProduct="openProduct"
          :editing="editing"
          :orderDepartmentCode="orderFull.departamentoCodigo"
          :priceLevel="orderFull.priceLevel"
          :productsQty="Number(orderFull.productsQuantity)"
          :productsLines="Number(orderFull.productsLines)"
          :canBeEdited="canBeEdited"
          :couponCode="orderFull.discountCoupon || ''"
        />
        <template v-if="editing !== 'productsBox'">
          <div class="order-tabs" style="grid-area: f;">
            <div class="order-tabs-bar" role="tablist">
              <button
                v-for="tab in bottomTabs"
                :key="tab.key"
                type="button"
                role="tab"
                class="order-tab"
                :class="{ 'is-active': selectedSection === tab.key }"
                :aria-selected="selectedSection === tab.key ? 'true' : 'false'"
                @click="selectedSection = tab.key"
              >
                <i class="mdi" :class="tab.icon"></i>
                <span>{{ $t(tab.label) }}</span>
                <span v-if="tab.count" class="tab-count">{{ tab.count }}</span>
              </button>
            </div>
            <div class="order-tab-panel">
              <OrderLogs
                v-if="selectedSection === 'order_logs'"
                :logs="orderFull.logs"
              />
              <OrderVersions
                v-if="selectedSection === 'order_versions'"
                :versions="orderFull.versions"
              />
              <OrderPayments
                v-if="selectedSection === 'order_payments'"
                class="payments"
                :payments="orderFull.payments"
                :paymentLogs="orderFull.paymentLogs"
                :orderNumber="Number(orderFull.orderNumber)"
                :pendingAmount="Number(orderFull.pendingPayment)"
                :orderStatus="orderFull.status"
                :canAddPayment="
                  orderFull.parentOrderNumber == null ||
                    orderFull.parentOrderNumber === orderFull.orderNumber
                "
                @deletedPayment="
                  id => {
                    orderFull.payments = orderFull.payments.filter(payment => {
                      return payment.id != id;
                    });
                    getOrder();
                  }
                "
                @createdPayment="handlePaymentCreated"
                @refundedPayment="getOrder()"
              />
              <Shipments
                v-if="selectedSection === 'shipments'"
                class="shipments"
                :shipments="orderFull.shipments"
                :orderNumber="orderFull.orderNumber"
                @addedShipment="addedShipment"
                @deletedShipment="deletedShipment"
              />
              <CustomerNotes
                v-if="selectedSection === 'customer_notes'"
                class="notes-customer w-full box"
                :notes="orderFull.customerNotes"
                :orderNumber="orderFull.orderNumber"
                @startEditing="updateEditing"
                @finishedEditing="finishedEditing"
                :editing="editing"
              />
              <ReturnList
                v-if="selectedSection === 'returns'"
                class="notes w-full box"
                :returns="orderFull.returns"
                :orderNumber="orderFull.orderNumber"
              />
              <Notes
                v-if="selectedSection === 'order_internal_notes'"
                class="notes w-full box"
                :notes="orderFull.internalNotes"
                :adminNotes="orderFull.adminNotes"
                :orderNumber="orderFull.orderNumber"
                @startEditing="updateEditing"
                @finishedEditing="finishedEditing"
                :editing="editing"
              />
              <InfoautoOrderMigrationData
                v-if="selectedSection === 'infoauto'"
                :orderNumber="orderFull.orderNumber"
                :infoautoData="orderFull.infoautoMigrations"
                :infoautoDataLogs="orderFull.infoautoMigrationsLogs"
              >
              </InfoautoOrderMigrationData>
            </div>
          </div>
        </template>
      </div>
      <div v-if="updatingOrder" class="updating-overlay">
        <div class="updating-indicator">
          <BaseMiniSpinner />
          <span>{{ $t("message.order.saving") }}</span>
        </div>
      </div>
    </div>
    <div
      v-else-if="isOpenModal && wrongDepartmentInfo && !loading"
      class="order-state wrong-department"
    >
      <div class="state-icon"><i class="mdi mdi-office-building"></i></div>
      <h2 class="state-title">Order #{{ wrongDepartmentInfo.orderNumber }}</h2>
      <p class="state-text">
        This order belongs to department
        <strong>{{ wrongDepartmentInfo.orderDepartmentName }}</strong
        >, but you are currently viewing
        <strong>{{ wrongDepartmentInfo.currentDepartment }}</strong
        >.
      </p>
      <div class="state-facts">
        <span>
          Status: <strong>{{ wrongDepartmentInfo.orderStatus }}</strong>
        </span>
        <span v-if="wrongDepartmentInfo.orderDate">
          Date: <strong>{{ wrongDepartmentInfo.orderDate }}</strong>
        </span>
        <span v-if="wrongDepartmentInfo.total != null">
          Total: <strong>{{ wrongDepartmentInfo.total }}</strong>
        </span>
      </div>
      <button
        type="button"
        class="ob-btn ob-btn-primary"
        @click="switchToOrderDepartment"
      >
        <i class="mdi mdi-swap-horizontal"></i>
        Switch to {{ wrongDepartmentInfo.orderDepartmentName }}
      </button>
      <div
        v-if="wrongDepartmentInfo.items && wrongDepartmentInfo.items.length"
        class="state-items"
      >
        <table>
          <thead>
            <tr>
              <th class="w-12"></th>
              <th>Code</th>
              <th>Description</th>
              <th class="text-right">Qty</th>
              <th class="text-right">Price</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in wrongDepartmentInfo.items" :key="i">
              <td>
                <img
                  v-if="item.image"
                  :src="item.image"
                  class="w-10 h-10 object-contain"
                />
              </td>
              <td class="font-mono">{{ item.nkrCode }}</td>
              <td>{{ item.description }}</td>
              <td class="text-right">{{ item.quantity }}</td>
              <td class="text-right">{{ item.price }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div v-else-if="loading" class="loading-full order-loading">
      <BaseSmallSpinner />
    </div>
    <div v-else class="order-state">
      <div class="state-icon is-error">
        <i class="mdi mdi-alert-circle-outline"></i>
      </div>
      <h2 class="state-title">
        {{ $t("message.order.error_loading_order") }}
      </h2>
      <p class="state-text">
        {{ errorMessage || $t("message.order.order_not_loaded_hint") }}
      </p>
      <button
        v-if="orderUUID"
        type="button"
        class="ob-btn ob-btn-primary"
        @click="reloadOrder"
      >
        <i class="mdi mdi-refresh"></i>
        {{ $t("message.order.retry") }}
      </button>
    </div>
    <button
      id="nkrsync-button"
      v-if="nkrSyncEnabled && !isQuote"
      class="block absolute top-2/4 right-0 w-10 md:w-12 transform -translate-y-2/4"
      @click="showSyncModal = true"
      style="background-color: black; color: white; padding: 10px 0px; border-radius: 4px 0px 0px 4px; font-weight: 800; line-height: normal;"
    >
      <div class="letter-column">
        <div v-for="(letter, index) in 'nkrsync'" :key="index" class="letter">
          {{ letter }}
        </div>
      </div>
    </button>
    <!-- The new customer has a usual forwarder (GeneralInfo): ask to add it.
         Here and not in GeneralInfo: getOrder remounts the order view -->
    <BaseModal
      v-if="suggestedForwarder"
      :value="true"
      @input="suggestedForwarder = null"
    >
      <template v-slot:header>
        <h1>{{ $t("message.order.forwarder_address") }}</h1>
      </template>
      <p class="mb-4">
        {{
          $t("message.order.add_customer_forwarder_question", {
            name: suggestedForwarder.name
          })
        }}
      </p>
      <div class="flex flex-row justify-end gap-2">
        <button
          class="btn btn-success-button"
          :disabled="addingForwarder"
          @click="addSuggestedForwarder"
        >
          {{ $t("message.order.add_forwarder") }}
        </button>
        <button
          class="btn btn-dark-button"
          :disabled="addingForwarder"
          @click="suggestedForwarder = null"
        >
          {{ $t("message.order.not_now") }}
        </button>
      </div>
    </BaseModal>
    <ReactivateOrderModal
      v-if="showReactivateOrderModal"
      :saving="reactivatingOrder"
      @close="showReactivateOrderModal = false"
      @proceed="proceedReactivateOrder"
    />
    <CancelOrderModal
      v-if="showCancelOrderModal"
      @close="showCancelOrderModal = false"
      :saving="cancelingOrder"
      @proceedCancelOrder="proceedCancelOrder"
    />
    <CustomInvoiceModal
      v-model="showCustomInvoiceModal"
      v-if="orderFull"
      :orderNumber="orderFull ? orderFull.orderNumber : ''"
      :orderData="orderFull"
      @invoice-generated="handleInvoiceGenerated"
    />
  </BaseModal>
</template>
<script>
import { getOrderStatusKey } from "@/constants/orderStatusMap";
import GeneralInfo from "../order/GeneralInfo.vue";
import Totals from "../order/Totals.vue";
import ProductsBox from "../order/ProductsBox.vue";
import OrderLogs from "../order/OrderLogs.vue";
import OrderVersions from "../order/OrderVersions.vue";
import AddressManager from "../order/AddressManager.vue";
import OrderPayments from "../order/OrderPayments.vue";
import InfoautoOrderMigrationData from "../order/InfoautoOrderMigrationData.vue";
import Shipments from "../order/Shipments.vue";
import ReturnList from "../order/ReturnList.vue";
import Notes from "../order/Notes.vue";
import CustomerNotes from "../order/CustomerNotes.vue";
import SMSOrderModal from "../order/SMSOrderModal.vue";
import EmailOrderModal from "../order/EmailOrderModal.vue";
import OrderReport from "../order/OrderReport.vue";
import CancelOrderModal from "../order/CancelOrderModal.vue";
import ReactivateOrderModal from "../order/ReactivateOrderModal.vue";
import UnpaidOrderCommentModal from "../order/UnpaidOrderCommentModal.vue";
import AfterConfirmedOrder from "../order/AfterConfirmedOrder.vue";
import CustomInvoiceModal from "../order/CustomInvoiceModal.vue";
import BulkOrderUpload from "../order/BulkOrderUpload.vue";
import {
  getOrder,
  sendOrderEmail,
  completeDraftOrder,
  sendToWarehouseAllocation as sendToWarehouseAllocationApi,
  cancelOrder as cancelOrderApi,
  reactivateOrder as reactivateOrderApi,
  createOrderFromOrderId,
  updateOrder,
  allocateOrderProducts as allocateOrderProductsApi,
  verifyDropshipProducts as verifyDropshipProductsApi,
  verifyOrderProducts as verifyOrderProductsApi,
  splitDropshipOrder as splitDropshipOrderApi
} from "@/api/store/OrdersService.js";
import { mapGetters } from "vuex";
import { exportSingleOrderInfoauto } from "@/api/store/Infoauto.js";
import { verifyVinOrder } from "@/api/store/AiService.js";
import {
  convertQuoteToOrder,
  deleteQuote,
  markQuoteReady as markQuoteReadyApi,
  updateQuoteName,
  isQuoteNumber,
  quoteReference
} from "@/api/store/QuotesService.js";

export default {
  components: {
    GeneralInfo,
    CancelOrderModal,
    ReactivateOrderModal,
    UnpaidOrderCommentModal,
    AfterConfirmedOrder,
    CustomInvoiceModal,
    BulkOrderUpload,
    AddressManager,
    InfoautoOrderMigrationData,
    SMSOrderModal,
    EmailOrderModal,
    OrderReport,
    Totals,
    ReturnList,
    Shipments,
    OrderPayments,
    ProductsBox,
    OrderLogs,
    Notes,
    OrderVersions,
    CustomerNotes
  },
  props: {
    orderUUID: {
      type: [String, Number],
      required: true
    },
    value: {
      type: Boolean,
      required: true
    },
    hasNext: {
      type: Boolean,
      required: false,
      default: false
    },
    hasPrev: {
      type: Boolean,
      required: false,
      default: false
    },
    inline: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      showSyncModal: false,
      addressSection: "shipping-address",
      updatingOrder: false,
      editing: "",
      showProductModal: false,
      openedProduct: "",
      orderFull: null,
      isOpenModal: this.value,
      // Start as loading whenever the modal opens already populated (e.g. a
      // direct link to /order/:orderNumber) so the first render shows the
      // spinner instead of flashing the "order not found" state.
      loading: !!this.value,
      renderId: 0,
      // { infoautoId, name } from GeneralInfo after assigning a customer
      suggestedForwarder: null,
      addingForwarder: false,
      showInfoautoSearch: false,
      showSMSModal: false,
      showEmailModal: false,
      showOrderOptions: false,
      showReactivateOrderModal: false,
      reactivatingOrder: false,
      errorMessage: "",
      selectedSection: "order_internal_notes",
      exportingInfoauto: false,
      showCancelOrderModal: false,
      cancelingOrder: false,
      savedScrollPosition: 0,
      headerScrolled: false,
      mobileActiveTab: "general",
      showUnpaidCommentModal: false,
      pendingUnpaidComment: null,
      showAfterConfirmedModal: false,
      showCustomInvoiceModal: false,
      verifyingVin: false,
      vinVerification: null,
      wrongDepartmentInfo: null,
      convertingQuote: false,
      markingQuoteReady: false,
      // Same breakpoint as Tailwind's md (768px)
      isMobile: window.matchMedia("(max-width: 767px)").matches,
      editingQuoteName: false,
      quoteNameInput: "",
      mobileTabs: [
        {
          key: "general",
          icon: "mdi-information-outline",
          label: "message.order.tab_general"
        },
        {
          key: "address",
          icon: "mdi-map-marker-outline",
          label: "message.order.tab_address"
        },
        {
          key: "totals",
          icon: "mdi-calculator-variant",
          label: "message.order.tab_totals"
        }
      ]
    };
  },
  computed: {
    ...mapGetters("general", ["getAdminSettings"]),
    userCanUseAIFeatures() {
      return this.$store.getters["auth/userHasPermission"](
        "can_use_ia_features"
      );
    },
    // A quote is an order without an order number yet (negative number):
    // same edition, but no confirmation, payments or shipments until it is
    // converted. See QuotesService.
    isQuote() {
      return isQuoteNumber(
        this.orderFull ? this.orderFull.orderNumber : this.orderUUID
      );
    },
    headerReference() {
      const orderNumber = this.orderFull
        ? this.orderFull.orderNumber
        : this.orderUUID;
      return quoteReference(orderNumber);
    },
    translatedStatus() {
      if (this.isQuote) return this.$t("message.order.quote_label");
      if (!this.orderFull?.status) return "";
      const key = getOrderStatusKey(this.orderFull.status);
      if (key) {
        const translationKey = `message.order_status.${key}`;
        const translated = this.$t(translationKey);
        return translated !== translationKey
          ? translated
          : this.orderFull.status;
      }
      return this.orderFull.status;
    },
    nkrSyncEnabled() {
      const nkrSyncSetting = this.getAdminSettings.get("nkr_sync");
      return nkrSyncSetting?.value === "true" ? true : false;
    },
    // Color del badge de estado en el header
    statusTone() {
      if (this.isQuote) return "purple";
      switch (getOrderStatusKey(this.orderFull?.status)) {
        case "awaiting_payment":
          return "amber";
        case "awaiting_shipment":
        case "processing":
        case "warehouse_allocation":
        case "vin_verification":
          return "blue";
        case "shipped":
        case "delivered":
        case "dropship_processed":
          return "green";
        case "cancelled":
        case "cancel_request_pending":
          return "red";
        case "on_hold":
          return "orange";
        case "dropship":
        case "dropship_pending":
        case "local_dropship":
          return "purple";
        default:
          return "gray";
      }
    },
    headerCustomerName() {
      const order = this.orderFull;
      if (!order) return "";
      return (
        order.customerInfoauto?.generalInfo?.name ||
        order.customer?.generalInfo?.name ||
        order.shippingAddress?.name ||
        order.customerEmail ||
        ""
      );
    },
    hasAmountDue() {
      return Number(this.orderFull?.pendingPayment) > 0;
    },
    bottomTabs() {
      const order = this.orderFull;
      const count = list => (Array.isArray(list) ? list.length : 0);
      if (this.isQuote) {
        return [
          {
            key: "order_internal_notes",
            icon: "mdi-note-text-outline",
            label: "message.order.tab_internal_notes",
            count: count(order.adminNotes)
          },
          {
            key: "customer_notes",
            icon: "mdi-message-text-outline",
            label: "message.order.tab_customer_notes"
          },
          {
            key: "order_logs",
            icon: "mdi-history",
            label: "message.order.tab_quote_logs",
            count: count(order.logs)
          }
        ];
      }
      const tabs = [
        {
          key: "shipments",
          icon: "mdi-truck-outline",
          label: "message.order.tab_shipments",
          count: count(order.shipments)
        },
        {
          key: "order_payments",
          icon: "mdi-cash-multiple",
          label: "message.order.tab_order_payments",
          count: count(order.payments)
        },
        {
          key: "order_logs",
          icon: "mdi-history",
          label: "message.order.tab_order_logs",
          count: count(order.logs)
        },
        {
          key: "order_versions",
          icon: "mdi-file-pdf-box",
          label: "message.order.tab_order_versions",
          count: count(order.versions)
        },
        {
          key: "order_internal_notes",
          icon: "mdi-note-text-outline",
          label: "message.order.tab_internal_notes",
          count: count(order.adminNotes)
        },
        {
          key: "customer_notes",
          icon: "mdi-message-text-outline",
          label: "message.order.tab_customer_notes"
        },
        {
          key: "returns",
          icon: "mdi-keyboard-return",
          label: "message.order.tab_returns",
          count: count(order.returns)
        }
      ];
      if (this.infoautoExportEnabled) {
        tabs.push({
          key: "infoauto",
          icon: "mdi-sync",
          label: "message.order.tab_infoauto"
        });
      }
      return tabs;
    },
    infoautoExportEnabled() {
      const infoautoEnabled = this.getAdminSettings.get(
        "infoauto_orders_sync_enabled"
      );
      return infoautoEnabled?.value === "true" ? true : false;
    },
    hasProductsNoLine() {
      //find product without line
      if (
        this.orderFull.items.find(
          item => item.line === null || item.line === ""
        )
      ) {
        return true;
      } else {
        return false;
      }
    },
    errors() {
      if (!this.orderFull) return [];

      let errorsList = [];

      if (
        this.orderFull.statusPayment == "0" &&
        this.orderFull.status != "Draft"
      ) {
        errorsList.push(this.$t("message.order.error_order_not_paid"));

        const productsWithoutStock = this.orderFull.items.filter(
          item =>
            item.stockOnConfirmation !== null &&
            Number(item.stockOnConfirmation) < Number(item.quantity)
        );
        if (productsWithoutStock.length > 0) {
          errorsList.push(
            this.$t("message.order.error_products_without_stock")
          );
        }
      }
      // A quote can go out without shipping service or customer: they are
      // required when it becomes an order
      if (
        !this.isQuote &&
        (!this.orderFull.shippingPreference ||
          this.orderFull.shippingPreference == "")
      ) {
        errorsList.push(
          this.$t("message.order.error_shipping_service_not_selected")
        );
      }

      if (!this.isQuote && !this.orderFull.customerEmail) {
        errorsList.push(this.$t("message.order.error_no_email"));
      }
      if (
        this.orderFull.customerInfoauto &&
        this.orderFull.customerInfoauto.generalInfo.priceLevel !==
          this.orderFull.priceLevel
      ) {
        errorsList.push(
          this.$t("message.order.error_infoauto_price_level_mismatch")
        );
      }
      if (
        !this.orderFull.customerInfoauto &&
        this.orderFull.customer &&
        this.orderFull.customer.generalInfo.priceLevel !==
          this.orderFull.priceLevel
      ) {
        errorsList.push(
          this.$t("message.order.error_customer_price_level_mismatch")
        );
      }
      return errorsList;
    },
    warnings() {
      return this.orderFull?.warnings || [];
    },
    // El API manda warnings como objeto ({ requireSplit: "..." }), no como lista
    warningList() {
      return Object.values(this.warnings).filter(
        warning => typeof warning === "string" && warning
      );
    },
    orderCanBeSplitted() {
      const orderStatusAllowed = [
        "Draft",
        "Awaiting Payment",
        "Warehouse Allocation"
      ];
      if (
        this.orderFull &&
        orderStatusAllowed.includes(this.orderFull.status) &&
        this.orderFull.warnings?.requireSplit
      ) {
        return true;
      }
      return false;
    },
    isOpen() {
      return this.value;
    },
    orderPhone() {
      const address = this.orderFull?.shippingAddress;
      return `${address?.phoneAreaCode || ""}${address?.phone || ""}`;
    },
    currentUserPermissions() {
      return this.$store.getters["auth/currentUserPermissions"];
    },
    // order_confirmed_edition (y superadmin, que lo tiene todo) edita la orden
    // en cualquier estado, tambien despues de crear envios: es justo el permiso
    // que existe para corregir una orden ya confirmada.
    // Superadmin can undo a cancellation (Orders::reactivate_post)
    canReactivate() {
      return (
        !this.isQuote &&
        this.orderFull?.status === "Cancelled" &&
        this.$store.getters["auth/isSuperadmin"]
      );
    },
    userCanEditConfirmedOrder() {
      return this.$store.getters["auth/userHasPermission"](
        "order_confirmed_edition"
      );
    },
    canBeEdited() {
      if (!this.orderFull) return false;

      // A converted quote is the history of its order
      if (this.isQuote && this.orderFull.convertedOrderNumber) return false;

      const status = this.orderFull.status.toLowerCase();

      if (status == "warehouse_allocation") {
        return true;
      }

      if (this.userCanEditConfirmedOrder) {
        return true;
      }

      if (this.orderFull.shipments.length > 0) return false;

      return status == "draft" || status == "awaiting payment";
    },
    productsOnly() {
      return this.orderFull.items.filter(item => !item.kit || item.kit == "");
    },
    kitProducts() {
      const kits = new Map();
      this.orderFull.items.forEach(item => {
        if (item.kit && item.kit !== "") {
          if (!kits.has(item.kit)) {
            kits.set(item.kit, {
              kitCode: item.kit,
              kitImage: item.kitImage,
              kitItemsQuantityByKit: item.kitItemsQuantityByKit,
              kitPrice: item.kitPrice,
              kitQuantity: item.kitQuantity,
              quantity: item.kitQuantity,
              kitTitle: item.kitTitle,
              kitItems: []
            });
          }
          kits.get(item.kit).kitItems.push(item);
        }
      });
      return Array.from(kits.values());
    }
  },
  watch: {
    isOpenModal(val) {
      this.addressSection = "shipping-address";
      this.$emit("input", val);
    },
    value(val) {
      if (val && val !== "") {
        const query = { ...this.$route.query, orderOpen: this.orderUUID };
        this.$router.replace({ query }).catch(() => {});
        this.$nextTick(() => {
          this.headerScrolled = false;
          this.attachScrollListener();
        });
      } else {
        const query = { ...this.$route.query };
        delete query.orderOpen;
        this.$router.replace({ query }).catch(() => {});
        this.detachScrollListener();
      }
      this.isOpenModal = val;
    },
    showOrderOptions(open) {
      if (open) {
        document.addEventListener("click", this.onDocumentClick, true);
      } else {
        document.removeEventListener("click", this.onDocumentClick, true);
      }
    },
    orderUUID() {
      console.log("orderUUID changed");
      this.showOrderOptions = false;
      this.editingQuoteName = false;
      this.vinVerification = null;
      this.verifyingVin = false;
      if (this.orderUUID === "") {
        this.orderFull = null;
        this.loading = false;
        return;
      }
      this.loading = true;
      this.orderFull = null;
      this.getOrder().catch(() => {
        this.$toast.open({
          message: `Error on request. Please ask to your system administrator`,
          type: "error",
          position: "top-right"
        });
      });
    },
    editing(newVal, oldVal) {
      // Restore scroll position when exiting ProductsBox editing mode
      if (oldVal === "productsBox" && newVal !== "productsBox") {
        this.$nextTick(() => {
          this.restoreScrollPosition();
        });
      }
    }
  },
  mounted() {
    this.mobileQuery = window.matchMedia("(max-width: 767px)");
    this.onMobileQueryChange = e => {
      this.isMobile = e.matches;
    };
    this.mobileQuery.addListener(this.onMobileQueryChange);
    window.addEventListener("beforeunload", this.handleUnload);
    this.$nextTick(() => this.attachScrollListener());
    if (this.value) {
      this.loading = true;
      this.orderFull = null;
      this.getOrder().catch(() => {
        this.$toast.open({
          message: `Error on request. Please ask to your system administrator`,
          type: "error",
          position: "top-right"
        });
      });
    }
  },
  methods: {
    // Ready: the customer can pay it from the store with its prices for a
    // week; marking it again starts the week over
    markQuoteReady(ready) {
      if (this.editing !== "") {
        alert(
          `You are editing the section ${this.editing}, please confirm or cancel before continue.`
        );
        return;
      }
      const question = !ready
        ? "message.order.unmark_quote_ready_confirm"
        : this.orderFull.readyAt
        ? "message.order.renew_quote_ready_confirm"
        : "message.order.mark_quote_ready_confirm";
      if (
        !confirm(
          this.$t(question, { days: this.orderFull.quotePriceValidDays })
        )
      )
        return;
      this.markingQuoteReady = true;
      markQuoteReadyApi(this.orderFull.orderNumber, ready)
        .then(res => {
          this.orderFull.readyAt = res.readyAt;
          this.orderFull.pricesValidUntil = res.pricesValidUntil;
          this.$emit("orderUpdated");
        })
        .catch(e => {
          this.$toast.open({
            message: e?.response?.data?.message || "Error updating the quote",
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.markingQuoteReady = false;
        });
    },
    convertQuote() {
      if (this.editing !== "") {
        alert(
          `You are editing the section ${this.editing}, please confirm or cancel before continue.`
        );
        return;
      }
      if (!confirm(this.$t("message.order.convert_quote_confirm"))) return;
      this.convertingQuote = true;
      convertQuoteToOrder(this.orderFull.orderNumber)
        .then(res => {
          this.$toast.open({
            message: this.$t("message.order.quote_converted", {
              order: res.orderNumber
            }),
            type: "success",
            position: "top-right"
          });
          this.$emit("orderUpdated");
          this.$emit("quoteConverted", res.orderNumber);
        })
        .catch(e => {
          this.$toast.open({
            message: e?.response?.data?.message || "Error converting quote",
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.convertingQuote = false;
        });
    },
    removeQuote() {
      if (!confirm(this.$t("message.order.delete_quote_confirm"))) return;
      deleteQuote(this.orderFull.orderNumber)
        .then(() => {
          this.$toast.open({
            message: this.$t("message.order.quote_deleted"),
            type: "success",
            position: "top-right"
          });
          this.$emit("orderUpdated");
          this.$emit("quoteDeleted", this.orderFull.orderNumber);
          this.changeModalStatus(false);
        })
        .catch(e => {
          this.$toast.open({
            message: e?.response?.data?.message || "Error deleting quote",
            type: "error",
            position: "top-right"
          });
        });
    },
    startQuoteNameEdit() {
      this.quoteNameInput = this.orderFull.quoteName || "";
      this.editingQuoteName = true;
      this.$nextTick(
        () => this.$refs.quoteNameInput && this.$refs.quoteNameInput.focus()
      );
    },
    saveQuoteName() {
      if (!this.editingQuoteName) return;
      this.editingQuoteName = false;
      const name = this.quoteNameInput.trim();
      if (name === (this.orderFull.quoteName || "")) return;
      updateQuoteName(this.orderFull.orderNumber, name)
        .then(() => {
          this.orderFull.quoteName = name || null;
          this.$emit("orderUpdated");
        })
        .catch(() => {
          this.$toast.open({
            message: "Error saving quote name",
            type: "error",
            position: "top-right"
          });
        });
    },
    async verifyVin() {
      this.verifyingVin = true;
      this.vinVerification = null;
      try {
        const items = this.orderFull.items.map(item => ({
          nkrCode: item.nkrCode,
          description: item.description
        }));
        this.vinVerification = await verifyVinOrder(this.orderFull.vin, items);
      } catch (e) {
        this.$toast.open({
          message:
            e.response?.data?.error || e.message || "Error verifying VIN",
          type: "error",
          position: "top-right"
        });
      } finally {
        this.verifyingVin = false;
      }
    },

    getAddressForSection() {
      switch (this.addressSection) {
        case "shipping-address":
          return this.orderFull.shippingAddress;
        case "billing-address":
          return this.orderFull.billingAddress;
        case "forwarder-address":
          return this.orderFull.forwarderAddress || null;
        default:
          return null;
      }
    },
    bulkAddProduct(product) {
      if (this.$refs.productsBox) {
        if (!this.$refs.productsBox.edit) {
          this.$refs.productsBox.startEdit();
        }
        this.$refs.productsBox.addProduct(product);
      }
    },
    splitDropshipOrder() {
      if (this.loading) return;
      if (this.orderCanBeSplitted) {
        this.loading = true;
        splitDropshipOrderApi(this.orderFull.orderNumber)
          .then(res => {
            this.$toast.open({
              message: `Order splitted`,
              type: "success",
              position: "top-right"
            });
            alert(
              `Order ${res.data.join(
                ","
              )} has been created with the dropship products`
            );
            this.getOrder();
          })
          .catch(e => {
            this.$toast.open({
              message: e.response.data.message,
              type: "error",
              position: "top-right"
            });
          })
          .finally(() => {
            this.loading = false;
          });
      } else {
        alert("This order cannot be splitted");
      }
    },
    setNoProcessedDropship() {
      if (
        confirm(
          "Are you sure you want to set this order as not processed for dropship?"
        )
      ) {
        this.updatingOrder = true;
        updateOrder(this.orderFull.orderNumber, "dropship-processed", {
          dropshipProcessed: 0
        })
          .then(() => {
            this.getOrder();
            this.$emit("orderUpdated");
          })
          .catch(e => {
            this.$toast.open({
              message: e.response.data.message,
              type: "error",
              position: "top-right"
            });
          })
          .finally(() => {
            this.updatingOrder = false;
          });
      }
    },
    setProcessedDropship() {
      if (
        confirm(
          "Are you sure you want to set this order as processed for dropship?"
        )
      ) {
        this.updatingOrder = true;
        updateOrder(this.orderFull.orderNumber, "dropship-processed", {
          dropshipProcessed: 1
        })
          .then(() => {
            this.getOrder();
            this.$emit("orderUpdated");
          })
          .catch(e => {
            this.$toast.open({
              message: e.response.data.message,
              type: "error",
              position: "top-right"
            });
          })
          .finally(() => {
            this.updatingOrder = false;
          });
      }
    },
    updatedSameShippingAndBilling(value) {
      this.updatingOrder = true;
      updateOrder(this.orderFull.orderNumber, "same-shipping-billing", {
        sameShippingAndBilling: value
      })
        .then(res => {
          if (res.warnings) {
            res.warnings.forEach(element => {
              this.$toast.open({
                message: element,
                type: "info",
                position: "top-right",
                duration: 10000
              });
            });
          }
          this.getOrder();
        })
        .catch(e => {
          this.$toast.open({
            message: e.response.data.message,
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.updatingOrder = false;
          this.$emit("updateFinished", this.orderToEdit);
        });
    },
    changedAddressSection(section) {
      this.addressSection = section;
    },
    updateFinished(order) {
      this.orderFull = JSON.parse(JSON.stringify({ ...order }));
      this.updatingOrder = false;
    },
    toggleOrderOptions() {
      this.showOrderOptions = !this.showOrderOptions;
    },
    closeOrderOptions() {
      this.showOrderOptions = false;
    },
    // Con el menu abierto, Esc solo cierra el menu (sin esto BaseModal cerraba
    // la orden completa)
    handleOptionsEscape(e) {
      if (!this.showOrderOptions) return;
      e.stopPropagation();
      this.showOrderOptions = false;
    },
    // Click fuera del boton y del menu (el modal de OrderReport vive dentro del
    // menu, asi que usarlo no lo cierra)
    onDocumentClick(e) {
      const container = this.$refs.moreActions;
      if (container && !container.contains(e.target)) {
        this.showOrderOptions = false;
      }
    },
    reloadOrder() {
      this.loading = true;
      this.getOrder().catch(() => {
        this.loading = false;
      });
    },
    handleInvoiceGenerated(options) {
      console.log("Invoice generated with options:", options);
      // TODO: Handle invoice generation completion
    },
    createOrderFromCurrent() {
      if (
        confirm("do you want to create a new order with the same products?")
      ) {
        createOrderFromOrderId(this.orderFull.id)
          .then(res => {
            this.$toast.open({
              message: `The order ${res} has been created`,
              type: "success",
              position: "top-right"
            });
            this.$emit("showDraft");
          })
          .catch(() => {
            this.$toast.open({
              message: `Error creating order`,
              type: "error",
              position: "top-right"
            });
          });
      }
    },
    handleUnload(event) {
      if (this.editing !== "") {
        console.log("is editing");
        event.returnValue = "user is editing";
        return "user is editing";
      }

      console.log("no editing");
      return;
    },
    changeModalStatus(val) {
      if (this.editing != "") {
        alert(
          `You are editing the section ${this.editing}, please confirm or cancel before continue.`
        );
        return;
      }
      if (!val) {
        this.closeOrder();
      }
      this.isOpenModal = val;
    },
    saveScrollPosition() {
      // Get the scrollable container (modal content or order content)
      const scrollContainer = this.getScrollContainer();
      if (scrollContainer) {
        this.savedScrollPosition = scrollContainer.scrollTop;
        console.log("Saved scroll position:", this.savedScrollPosition);
      }
    },
    restoreScrollPosition() {
      // Get the scrollable container and restore scroll position
      const scrollContainer = this.getScrollContainer();
      if (scrollContainer && this.savedScrollPosition > 0) {
        // Use setTimeout to ensure DOM has fully updated after layout change
        setTimeout(() => {
          scrollContainer.scrollTop = this.savedScrollPosition;
          console.log("Restored scroll position:", this.savedScrollPosition);
        }, 50);
      }
    },
    attachScrollListener() {
      const container = this.getScrollContainer();
      if (container) {
        container.addEventListener("scroll", this.handleModalScroll);
      }
    },
    detachScrollListener() {
      const container = this.getScrollContainer();
      if (container) {
        container.removeEventListener("scroll", this.handleModalScroll);
      }
    },
    handleModalScroll(e) {
      // Histéresis: usamos dos umbrales con una zona muerta entre 20 y 60px
      // para evitar el "flickering" del header. Sin esto, al compactarse el
      // header cambia la geometría del scroll y scrollTop vuelve a cruzar un
      // umbral único, provocando que el header oscile entre grande/pequeño.
      const scrollTop = e.target.scrollTop;
      if (!this.headerScrolled && scrollTop > 60) {
        this.headerScrolled = true;
      } else if (this.headerScrolled && scrollTop < 20) {
        this.headerScrolled = false;
      }
    },
    getScrollContainer() {
      // Try to find the scrollable container
      // First try the ref to order content
      if (this.$refs.orderContent && this.$refs.orderContent.parentElement) {
        console.log(
          "Using parent of orderContent ref for scroll",
          this.$refs.orderContent.parentElement
        );
        return this.$refs.orderContent.parentElement;
      }

      // Fallback to modal content
      const modalContent = this.$el?.querySelector(".modal-content");
      if (modalContent) {
        console.log("Using modal-content for scroll", modalContent);
        return modalContent;
      }
      console.log("No specific scroll container found, using default");
      // Last fallback to the modal element itself
      return this.$el;
    },
    sendOrderEmail() {
      if (
        confirm(
          "are you sure you want to send this order to your customer by email?"
        )
      ) {
        sendOrderEmail(this.orderFull.orderNumber)
          .then(() => {
            this.$toast.open({
              message: `Order email sent!`,
              type: "success",
              position: "top-right"
            });
          })
          .catch(() => {
            this.$toast.open({
              message: `Error on request. Please ask to your system administrator`,
              type: "error",
              position: "top-right"
            });
          });
      }
    },
    OrderSystemStatus(systemUUID) {
      const found = this.orderFull.exportStatus.find(element => {
        return element.uuid === systemUUID && element.status === "success";
      });
      return found ? true : false;
    },
    closeOrder() {
      this.openedProduct = "";
      this.showProductModal = false;
      this.orderFull = null;
      // Reset saved scroll position when closing order
      this.savedScrollPosition = 0;
      console.log("close order");
      this.$emit("closeOrder");
    },
    async getOrder() {
      try {
        const result = await getOrder(this.orderUUID);
        if (result && result.status === "wrong_department") {
          this.wrongDepartmentInfo = result;
          this.orderFull = null;
        } else {
          this.orderFull = result;
          this.wrongDepartmentInfo = null;
        }
      } catch (e) {
        // Sin response (red caida) e.response.data tiraba otro error y el
        // spinner quedaba para siempre
        this.errorMessage =
          e?.response?.data?.message || e?.message || "Error on request";
        this.$toast.open({
          message: this.errorMessage,
          type: "error",
          position: "top-right"
        });
      }
      this.renderId++;
      this.loading = false;
    },
    async switchToOrderDepartment() {
      const departments = this.$store.getters["general/getDepartments"];
      const target = departments.find(
        d => d.code === this.wrongDepartmentInfo.orderDepartment
      );
      if (target) {
        await this.$store.dispatch("general/setDepartment", target);
        const params = { ...this.$route.params, departmentCode: target.code };
        const query = {
          ...this.$route.query,
          orderOpen: this.wrongDepartmentInfo.orderNumber
        };
        this.$router
          .push({ name: this.$route.name, params, query })
          .catch(err => {
            if (err.name !== "NavigationDuplicated") {
              console.error("Navigation error:", err);
            }
          });
        await this.getOrder();
      }
    },
    openProduct(code) {
      this.openedProduct = code;
      this.showProductModal = true;
    },
    updateEditing(value) {
      if (value === "productsBox" && this.editing !== "productsBox") {
        this.saveScrollPosition();
      }
      this.editing = value;
    },
    addSuggestedForwarder() {
      this.addingForwarder = true;
      // The API puts the forwarder's default address and phone
      updateOrder(this.orderFull.orderNumber, "forwarder", {
        infoautoCustomerId: this.suggestedForwarder.infoautoId,
        addressId: null,
        address: null,
        useDefaultAddress: true
      })
        .then(res => {
          (res?.warnings || []).forEach(message => {
            this.$toast.open({ message, type: "info", position: "top-right" });
          });
          this.suggestedForwarder = null;
          this.finishedEditing(true);
        })
        .catch(e => {
          this.$toast.open({
            message: e.response?.data?.message || e.message,
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.addingForwarder = false;
        });
    },
    async finishedEditing(updateOrder) {
      const wasEditingProducts = this.editing === "productsBox";
      this.editing = "";
      if (updateOrder) {
        this.$emit("orderUpdated");
        await this.getOrder();
      }
      this.updatingOrder = false;

      // Fallback: restore scroll position if we were editing products
      if (wasEditingProducts) {
        this.$nextTick(() => {
          this.restoreScrollPosition();
        });
      }
    },
    addedShipment(newShipment) {
      this.orderFull.shipments.push(newShipment);
      this.$emit("orderUpdated");
      this.getOrder();
    },
    deletedShipment(id) {
      console.log(id);
      this.orderFull.shipments = this.orderFull.shipments.filter(
        shipment => shipment.id != id
      );
    },
    confirmDraftOrder(split = false) {
      if (this.editing !== "") {
        alert(
          `You are editing the section ${this.editing}, please confirm or cancel before Confirm the order.`
        );
        return;
      }

      // if (this.hasProductsNoLine) {
      //   alert(
      //     "Error on confirm. You have products without line, you can relocate products and try again"
      //   );
      //   return;
      // }

      this.loading = true;
      completeDraftOrder(this.orderFull.orderNumber, split)
        .then(() => {
          this.$toast.open({
            message: "Order confirmed successfully!",
            type: "success",
            position: "top-right"
          });
          // Reload order data to get updated status
          this.getOrder().then(() => {
            // Show the after confirmed modal
            this.showAfterConfirmedModal = true;
          });
        })
        .catch(err => {
          this.$toast.open({
            message: err.response.data,
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    moveToAwaitingShipment() {
      const isNotPaid = this.orderFull.statusPayment == "0";

      // Check if customer has sufficient credit available
      const hasInfoautoCredit =
        this.orderFull.customerInfoauto?.credit?.mtoDispCia !== undefined &&
        this.orderFull.customerInfoauto.credit.mtoDispCia >=
          this.orderFull.total;

      // If customer has sufficient credit, proceed without payment check.
      // The automatic message is saved to the order logs, not as a note.
      if (hasInfoautoCredit) {
        const logMessage = `Client has credit available, order moved automatically.`;
        this.proceedWithAwaitingShipment(null, logMessage);
        return;
      }

      // If order is not paid, show modal to collect comment
      if (isNotPaid) {
        this.showUnpaidCommentModal = true;
        return;
      }

      // For paid orders, proceed with standard confirmation
      this.proceedWithAwaitingShipment(null);
    },
    handleUnpaidComment(comment) {
      // Close the modal
      this.showUnpaidCommentModal = false;

      // Store comment and proceed
      this.pendingUnpaidComment = comment;

      this.proceedWithAwaitingShipment(comment);
    },
    proceedWithAwaitingShipment(comment, logMessage = null) {
      // Standard confirmation for paid orders (auto-credit moves carry a logMessage)
      if (!comment && !logMessage) {
        if (
          !confirm(
            "Are you sure you want to move this order to awaiting shipment?"
          )
        ) {
          return;
        }
      }

      // Check for products without stock (dropship check)
      const productsWithoutStock = this.orderFull.items.filter(
        item => Number(item.stockOnConfirmation) < Number(item.quantity)
      );

      if (productsWithoutStock.length > 0) {
        if (
          !confirm("Esta orden parece dropship seguro que quieres continuar?")
        ) {
          this.pendingUnpaidComment = null;
          return;
        }
      }

      // Update order status with optional comment / automatic log message
      this.updateOrderStatus("Awaiting Shipment", comment, logMessage);
      this.pendingUnpaidComment = null;
    },
    updateOrderStatus(newStatus, comment = null, logMessage = null) {
      this.updatingOrder = true;
      const orderUpdates = {
        status: newStatus
      };

      // Add comment if provided (saved as an order note)
      if (comment) {
        orderUpdates.comment = comment;
      }

      // Add automatic log message if provided (saved to the order logs)
      if (logMessage) {
        orderUpdates.logMessage = logMessage;
      }

      updateOrder(this.orderFull.orderNumber, "order-status", orderUpdates)
        .then(res => {
          if (res.warnings) {
            res.warnings.forEach(element => {
              this.$toast.open({
                message: element,
                type: "info",
                position: "top-right",
                duration: 10000
              });
            });
          }
          this.loading = true;
          this.getOrder();
        })
        .catch(e => {
          this.$toast.open({
            message: e.response.data.message,
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.updatingOrder = false;
        });
    },
    sendToInfoauto() {
      if (this.infoautoExportEnabled && !this.exportingInfoauto) {
        this.exportingInfoauto = true;
        exportSingleOrderInfoauto(this.orderFull.orderNumber)
          .then(res => {
            console.log(res);
            this.$toast.open({
              message: `Order sent to Infoauto`,
              type: "success",
              position: "top-right"
            });
            this.getOrder();
          })
          .catch(err => {
            this.$toast.open({
              message: err.response.data,
              type: "error",
              position: "top-right"
            });
            this.getOrder();
          })
          .finally(() => {
            this.exportingInfoauto = false;
          });
      }
    },
    verifyOrderProducts() {
      this.loading = true;
      verifyOrderProductsApi(this.orderFull.orderNumber)
        .then(() => {
          this.getOrder();
          this.$toast.open({
            message: `Products verified`,
            type: "success",
            position: "top-right"
          });
        })
        .catch(() => {
          this.$toast.open({
            message: `Error on request. Please ask to your system administrator`,
            type: "error",
            position: "top-right"
          });
        });
    },
    cancelOrder() {
      if (this.orderFull.status !== "Draft") {
        this.showOrderOptions = false;
        this.showCancelOrderModal = true;
        console.log(this.orderFull.status);
        return;
      } else if (
        !window.confirm(
          "Do you want to cancel this order? this cant be reverted."
        )
      ) {
        return;
      }
      this.proceedCancelOrder("No reason needed, draft order cancelled");
    },
    proceedReactivateOrder(reason) {
      this.reactivatingOrder = true;
      reactivateOrderApi(this.orderFull.orderNumber, reason)
        .then(res => {
          this.$toast.open({
            message: this.$t("message.order.order_reactivated", {
              status: res.newStatus
            }),
            type: "success",
            position: "top-right"
          });
          this.showReactivateOrderModal = false;
          this.finishedEditing(true);
        })
        .catch(e => {
          this.$toast.open({
            message: e.response?.data?.message || e.message,
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.reactivatingOrder = false;
        });
    },
    proceedCancelOrder(msg) {
      this.cancellingOrder = true;
      this.showOrderOptions = false;
      cancelOrderApi(this.orderFull.orderNumber, msg)
        .then(() => {
          this.$toast.open({
            message: "order cancelled",
            type: "success",
            position: "top-right"
          });
          this.finishedEditing(true);
        })
        .catch(err => {
          this.$toast.open({
            message: err.response.data,
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.showCancelOrderModal = false;
          this.loading = false;
          this.cancellingOrder = false;
        });
    },
    sendToWarehouseAllocation() {
      if (this.editing !== "") {
        alert(
          `You are editing the section ${this.editing}, please confirm or cancel before Confirm the order.`
        );
        return;
      }
      this.loading = true;
      sendToWarehouseAllocationApi(this.orderFull.orderNumber)
        .then(() => {
          this.$toast.open({
            message: "order updated",
            type: "success",
            position: "top-right"
          });
          this.finishedEditing(true);
        })
        .catch(err => {
          this.$toast.open({
            message: err.response.data,
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    verifyDropshipProducts() {
      this.showOrderOptions = false;
      this.loading = true;
      verifyDropshipProductsApi(this.orderFull.orderNumber)
        .then(() => {
          this.getOrder();
          this.$toast.open({
            message: `order verified`,
            type: "success",
            position: "top-right"
          });
        })
        .catch(() => {
          this.$toast.open({
            message: `Error on request. Please ask to your system administrator`,
            type: "error",
            position: "top-right"
          });
        });
    },
    allocateOrderProducts() {
      this.showOrderOptions = false;
      this.loading = true;
      allocateOrderProductsApi(this.orderFull.orderNumber)
        .then(() => {
          this.getOrder();
          this.$toast.open({
            message: `Products allocated`,
            type: "success",
            position: "top-right"
          });
        })
        .catch(() => {
          this.$toast.open({
            message: `Error on request. Please ask to your system administrator`,
            type: "error",
            position: "top-right"
          });
        });
    },
    handleEmailSent() {
      console.log("Email sent successfully");
      // Optionally reload order or update state
    },
    handlePaymentAdded(paymentData) {
      console.log("Payment added:", paymentData);
      // Reload order to reflect new payment
      this.getOrder();
    },
    async handlePaymentCreated(payment) {
      console.log("Payment created:", payment);

      // Add payment to local array immediately
      this.orderFull.payments.push(payment);

      // Show loading state
      this.updatingOrder = true;

      // Poll for order status update after Stripe payment
      await this.pollForOrderUpdate();

      // Hide loading state
      this.updatingOrder = false;
    },
    async pollForOrderUpdate() {
      const maxAttempts = 10; // Try for up to 15 seconds
      const pollInterval = 1000; // Check every second
      let attempts = 0;

      while (attempts < maxAttempts) {
        attempts++;

        try {
          // Fetch the latest order data
          await this.getOrder();

          // Check if the order status has been updated (not "processing" anymore)
          // or if payment status has changed
          if (
            this.orderFull &&
            this.orderFull.statusPayment !== "0" &&
            this.orderFull.status !== "Processing"
          ) {
            console.log("Order payment status updated successfully");
            this.$toast.open({
              message: "Order payment status updated!",
              type: "success",
              position: "top-right"
            });
            break;
          }
        } catch (error) {
          console.error("Error polling for order update:", error);
        }

        // Wait before next attempt
        if (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, pollInterval));
        }
      }

      if (attempts >= maxAttempts) {
        console.log("Polling timeout - order may still be processing");
        this.$toast.open({
          message:
            "Payment processed. Order status may take a moment to update.",
          type: "info",
          position: "top-right"
        });
      }
    },
    handleShowStripeModal() {
      // This will be implemented when you provide the Stripe component
      this.$toast.open({
        message: "Stripe payment integration - to be implemented",
        type: "info",
        position: "top-right"
      });
      console.log("Show Stripe modal - waiting for Stripe component");
    },
    handleAfterConfirmSkip() {
      console.log("User skipped post-confirmation actions");
      this.$emit("orderUpdated");
    },
    handleAfterConfirmComplete() {
      console.log("User completed post-confirmation actions");
      this.$emit("orderUpdated");
      // Reload order to get latest state
      this.getOrder();
    },
    handleContinueAsCredit() {
      console.log("User chose to continue order as credit");
      this.$emit("orderUpdated");
    }
  },
  beforeRouteLeave(to, from, next) {
    console.log("beforeRouteLeave");
    if (this.isEditing !== "") {
      if (!window.confirm("Leave without saving?")) {
        return;
      }
    }
    next();
  },
  beforeDestroy() {
    if (this.mobileQuery) {
      this.mobileQuery.removeListener(this.onMobileQueryChange);
    }
    window.removeEventListener("beforeunload", this.handleUnload);
    document.removeEventListener("click", this.onDocumentClick, true);
    this.detachScrollListener();
  }
};
</script>
<style lang="scss">
@import "@/assets/style/css/generic-variables.scss";

// Estos estilos no son scoped: el header vive en BaseModal y las tarjetas en
// los subcomponentes. Todo lo propio del modal va dentro de .order-modal para
// no filtrarse al resto de la app. Los estados usan .is-active y no .active,
// que _base-modal.vue fuerza globalmente a display: block.

$ob-border: #e5e7eb;
$ob-muted: #6b7280;
$ob-text: #111827;
// Lienzo oscuro: sobre gris claro las tarjetas blancas no se distinguian y la
// pantalla se leia como una sola hoja. Todo lo que se apoya en el lienzo
// (cabecera, toolbar, tarjetas, pestanas) ya es una superficie blanca, asi que
// el contraste lo da el fondo.
$ob-canvas: #1f2937;
// Una cotizacion usa el mismo modal que una orden: el lienzo violeta (el tono
// de su badge) evita confundirlas de un vistazo
$ob-quote-canvas: #2e1065;
$ob-quote-accent: #7c3aed;
$ob-canvas-scrim: rgba(17, 24, 39, 0.55);
$ob-accent-soft: rgba(18, 110, 106, 0.08);
$ob-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
// Sobre el lienzo oscuro una sombra suave no se ve: las tarjetas necesitan
// sombra propia para despegarse del fondo
$ob-card-shadow: 0 1px 2px rgba(0, 0, 0, 0.3),
  0 10px 24px -14px rgba(0, 0, 0, 0.5);
// Banda de la cabecera de cada caja, para separar titulo de contenido
$ob-card-header-bg: #f8fafc;

.loading-full {
  height: 100%;
  margin: auto;
  display: flex;
  justify-content: center;
  align-items: center;
}

.box {
  min-width: 300px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 767px) {
    min-width: 0;
    width: 100%;
    max-width: 100%;
  }
}

.payments {
  grid-area: e;
}

.shipments {
  grid-area: f;
}

.order-content {
  display: grid;
  grid-gap: 16px;

  // Desde 1280px: general | direccion | totales
  @media (min-width: 1280px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.3fr) minmax(0, 1fr);
    grid-template-areas:
      "a b c"
      "d d d"
      "e e e"
      "f f f"
      "g g g"
      "h h h";
  }

  // Pantallas 2K+: las pestañas (envios, pagos, logs...) suben a una columna
  // lateral junto a las cajas superiores y los productos; así la tabla de
  // productos no se estira a todo el ancho
  @media (min-width: 2000px) {
    grid-template-columns:
      minmax(0, 1fr) minmax(0, 1.3fr) minmax(0, 1fr)
      minmax(0, 1.6fr);
    grid-template-areas:
      "a b c f"
      "d d d f"
      "e e e e"
      "g g g g"
      "h h h h";
  }

  // Tablet / laptop chico: tres columnas quedaban de ~250px; general y totales
  // arriba y la direccion a todo el ancho
  @media (min-width: 768px) and (max-width: 1279px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-template-areas:
      "a c"
      "b b"
      "d d"
      "e e"
      "f f"
      "g g"
      "h h";
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      "a"
      "b"
      "c"
      "d"
      "e"
      "f"
      "g"
      "h";
    gap: 10px;
  }
}

.order-content.editing-products {
  display: flex;
  flex-direction: column;

  // Editando, los productos quedan solos: sin tope se estiraban a todo el
  // ancho del modal en 2K/ultrawide
  @media (min-width: 2000px) {
    max-width: 1920px;
    margin-inline: auto;
  }
}

.order-view {
  width: 100%;

  .product-box {
    grid-area: d;
  }

  .notes {
    grid-area: g;
  }

  .notes-customer {
    grid-area: h;
  }

  .product-box,
  .custom-box {
    width: 100%;
    min-height: 200px;

    .table-full {
      width: 100%;

      @media (max-width: 767px) {
        overflow-x: auto;
      }
    }

    @media (max-width: 390px) {
      min-height: auto;
    }
  }

  .custom-box {
    .content {
      div {
        width: 45%;
      }
    }
  }
}

.order-modal {
  // Usa todo el espacio disponible (antes: 90px arriba y ~5% por lado)
  &.modal:not(.full-page) {
    padding: 12px;

    > .modal-content {
      width: 100%;
      max-width: 1920px;
      height: 100%;
      max-height: none;
      margin: 0 auto;
      border-radius: 12px;
      overflow-y: auto;
    }

    // En 2K+ el layout suma una cuarta columna: se aprovecha el ancho completo
    @media screen and (min-width: 2000px) {
      > .modal-content {
        max-width: none;
      }
    }

    @media screen and (max-width: 767px) {
      padding: 0;

      > .modal-content {
        height: 100vh;
        height: 100dvh;
        border-radius: 0;
      }
    }
  }

  &.is-quote > .modal-content {
    background-color: $ob-quote-canvas;

    > .header.hasinfo {
      border-top: 4px solid $ob-quote-accent;
    }
  }

  // Solo el contenido propio: en descendiente, el lienzo oscuro se colaba en
  // los modales anidados y dejaba su texto negro sobre fondo oscuro
  > .modal-content {
    background-color: $ob-canvas;
    font-size: 16px;
    // El contenedor del scroll es oscuro: la barra clara de serie desentonaba
    scrollbar-color: #4b5563 transparent;

    &::-webkit-scrollbar {
      width: 12px;
      height: 12px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      border: 3px solid transparent;
      border-radius: 999px;
      background-color: #4b5563;
      background-clip: content-box;

      &:hover {
        background-color: #6b7280;
      }
    }

    > .header.hasinfo {
      display: block;
      min-height: 0;
      padding: 12px 64px 12px 20px;
      background: white;
      border-bottom: 1px solid $ob-border;
      box-shadow: $ob-shadow;

      .info {
        color: $ob-text;
      }

      @media (max-width: 767px) {
        padding: 8px 48px 8px 12px;
      }
    }

    > .header .close {
      top: 50%;
      right: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      padding: 0;
      border-radius: 10px;
      transform: translateY(-50%);
      font-size: 28px;
      font-weight: 400;
      line-height: 1;
      color: $ob-muted;

      &:hover {
        background: #f3f4f6;
        color: $ob-text;
      }

      @media (max-width: 767px) {
        right: 6px;
        width: 36px;
        height: 36px;
      }
    }
  }

  /* ---------- Header ---------- */
  .order-header {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &.is-compact .order-number {
      font-size: 16px;
    }

    @media (max-width: 767px) {
      gap: 4px;
    }
  }

  .order-header-main {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 12px;
    min-width: 0;
  }

  .order-number {
    margin: 0;
    font-size: 20px;
    font-weight: 500;
    line-height: 1.2;
    color: $ob-muted;
    transition: font-size 0.2s;

    span {
      font-weight: 700;
      color: $ob-text;
    }

    @media (max-width: 767px) {
      font-size: 17px;
    }
  }

  .status-badge,
  .department-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
    white-space: nowrap;

    @media (max-width: 767px) {
      padding: 1px 8px;
      font-size: 14px;
    }
  }

  .status-badge {
    background: #f3f4f6;
    color: #374151;

    .status-dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: currentColor;
    }

    &.tone-amber {
      background: #fef3c7;
      color: #92400e;
    }

    &.tone-blue {
      background: #dbeafe;
      color: #1e40af;
    }

    &.tone-green {
      background: #dcfce7;
      color: #166534;
    }

    &.tone-red {
      background: #fee2e2;
      color: #991b1b;
    }

    &.tone-orange {
      background: #ffedd5;
      color: #9a3412;
    }

    &.tone-purple {
      background: #ede9fe;
      color: #5b21b6;
    }
  }

  // El departamento sigue en rojo (antes texto rojo 2xl): importa no confundirlo
  .department-pill {
    gap: 4px;
    border: 1px solid #fecaca;
    background: #fef2f2;
    color: #b91c1c;

    i {
      font-size: 18px;
    }
  }

  // Nombre de la cotizacion: se edita en el mismo header
  .quote-name {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 360px;
    padding: 2px 6px;
    border: 1px dashed transparent;
    border-radius: 6px;
    background: none;
    font-size: 16px;
    font-weight: 600;
    color: $ob-text;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    i {
      font-size: 14px;
      color: $ob-muted;
    }

    &.is-empty {
      font-weight: 400;
      color: $ob-muted;
    }

    &:hover {
      border-color: #d1d5db;
    }
  }

  .quote-name-input {
    width: 280px;
    max-width: 100%;
    padding: 3px 8px;
    border: 1px solid #93c5fd;
    border-radius: 6px;
    font-size: 16px;
    outline: none;
  }

  .order-header-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 18px;
    font-size: 16px;
    font-weight: 400;
    color: $ob-muted;

    .meta-item {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      white-space: nowrap;

      i {
        font-size: 18px;
      }

      strong {
        font-weight: 600;
        color: $ob-text;
      }
    }

    .meta-payment.is-due,
    .meta-payment.is-due strong {
      color: #b91c1c;
    }

    .meta-payment.is-paid {
      color: #15803d;
    }

    // Movil: cliente, fecha, total y saldo ocupaban 3 lineas de la cabecera fija
    @media (max-width: 767px) {
      gap: 2px 12px;
      font-size: 14px;

      .meta-item i {
        font-size: 16px;
      }
    }
  }

  /* ---------- Toolbar ---------- */
  .order-view {
    padding-top: 12px;

    @media (max-width: 767px) {
      padding-top: 8px;
    }
  }

  .order-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px 16px;
    margin: 0 16px;
    padding: 10px 12px;
    background: white;
    border: 1px solid $ob-border;
    border-radius: 12px;
    box-shadow: $ob-shadow;

    @media (max-width: 767px) {
      gap: 6px;
      margin: 0 8px;
      padding: 6px;
    }
  }

  .toolbar-related {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 8px;
    min-width: 0;
  }

  .related-label {
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: $ob-muted;
  }

  .related-chip {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    height: 28px;
    padding: 0 10px;
    border: 1px solid $ob-border;
    border-radius: 999px;
    background: #f9fafb;
    font-size: 16px;
    font-weight: 600;
    color: $link-color-1;
    transition: background 0.15s, border-color 0.15s;

    i {
      font-size: 18px;
    }

    &:hover {
      border-color: $link-color-1;
      background: $ob-accent-soft;
    }
  }

  .toolbar-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    margin-left: auto;

    // Movil: una sola fila que se desliza. Con varios estados disponibles los
    // botones ocupaban 3-4 filas antes de llegar a la orden. Opciones queda
    // pegado a la derecha como icono; su menu es fixed (hoja inferior), asi
    // que el overflow de la fila no lo recorta
    @media (max-width: 767px) {
      flex-wrap: nowrap;
      justify-content: flex-start;
      gap: 6px;
      width: 100%;
      overflow-x: auto;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }

      > .ob-btn {
        flex: 0 0 auto;
      }

      > .more-actions {
        position: sticky;
        right: 0;
        flex: 0 0 auto;
        margin-left: auto;
        padding-left: 6px;
        background: white;
        box-shadow: -10px 0 8px -8px rgba(16, 24, 40, 0.25);

        .more-actions-label,
        .chevron {
          display: none;
        }

        > .ob-btn {
          width: 40px;
          padding: 0;
        }
      }
    }
  }

  .ob-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 36px;
    padding: 0 14px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: white;
    font-size: 16px;
    font-weight: 500;
    color: #374151;
    white-space: nowrap;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s;

    i {
      font-size: 18px;
      line-height: 1;
    }

    &:hover:not(:disabled) {
      border-color: #9ca3af;
      background: #f9fafb;
      color: $ob-text;
    }

    &:focus-visible {
      outline: 2px solid $link-color-1;
      outline-offset: 2px;
    }

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }

    .chevron {
      margin-left: -2px;
      font-size: 16px;
      transition: transform 0.15s;
    }

    &.is-open {
      border-color: #9ca3af;
      background: #f3f4f6;

      .chevron {
        transform: rotate(180deg);
      }
    }

    .btn-spinner {
      width: 16px;
      height: 16px;
    }

    @media (max-width: 767px) {
      height: 40px;
      padding: 0 12px;
      font-size: 15px;
    }
  }

  .ob-btn-primary {
    border-color: $link-color-1;
    background: $link-color-1;
    color: white;

    &:hover:not(:disabled) {
      border-color: darken($link-color-1, 6%);
      background: darken($link-color-1, 6%);
      color: white;
    }
  }

  .ob-btn-success {
    border-color: #16a34a;
    background: #16a34a;
    color: white;

    &:hover:not(:disabled) {
      border-color: #15803d;
      background: #15803d;
      color: white;
    }
  }

  .ob-btn-danger {
    border-color: #fca5a5;
    color: #b91c1c;

    &:hover:not(:disabled) {
      border-color: #f87171;
      background: #fef2f2;
      color: #991b1b;
    }
  }

  .ob-btn-ai {
    border-color: #6c63ff;
    background: #1e1e2e;
    color: #c4b5fd;

    &:hover:not(:disabled) {
      border-color: #6c63ff;
      background: #6c63ff;
      color: white;
    }
  }

  /* ---------- Menu de opciones ---------- */
  .more-actions {
    position: relative;
  }

  // Sin transform en la animacion: OrderReport monta un BaseModal fixed adentro
  .actions-menu {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: 50;
    width: 310px;
    max-height: min(70vh, 640px);
    overflow-y: auto;
    padding: 6px;
    background: white;
    border: 1px solid $ob-border;
    border-radius: 12px;
    box-shadow: 0 12px 32px rgba(16, 24, 40, 0.16),
      0 2px 6px rgba(16, 24, 40, 0.06);
    text-align: left;
    white-space: normal;
    animation: ob-fade-in 0.12s ease-out;

    // Movil: hoja inferior
    @media (max-width: 767px) {
      position: fixed;
      top: auto;
      right: 8px;
      bottom: 8px;
      left: 8px;
      width: auto;
      max-height: 75vh;
    }
  }

  .menu-group {
    padding: 4px 0;

    & + .menu-group {
      border-top: 1px solid #f3f4f6;
    }
  }

  .menu-group-title {
    padding: 6px 10px 4px;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #9ca3af;
  }

  .menu-item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-height: 36px;
    padding: 6px 10px;
    border-radius: 8px;
    font-size: 16px;
    color: #374151;
    text-align: left;
    cursor: pointer;

    i {
      flex-shrink: 0;
      font-size: 18px;
      color: $ob-muted;
    }

    &:hover:not(:disabled):not(.is-static) {
      background: #f3f4f6;
      color: $ob-text;

      i {
        color: $link-color-1;
      }
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &.is-static {
      cursor: default;
      color: #15803d;

      i {
        color: #15803d;
      }
    }

    &.is-danger {
      color: #b91c1c;

      i {
        color: #dc2626;
      }

      &:hover:not(:disabled) {
        background: #fef2f2;
        color: #991b1b;

        i {
          color: #dc2626;
        }
      }
    }

    @media (max-width: 767px) {
      min-height: 44px;
    }
  }

  /* ---------- Avisos ---------- */
  .order-alerts {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 8px;
    margin: 12px 16px 0;

    @media (max-width: 767px) {
      grid-template-columns: 1fr;
      gap: 6px;
      margin: 8px 8px 0;
    }
  }

  .order-alert {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 8px 12px;
    border: 1px solid;
    border-radius: 10px;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.35;

    @media (max-width: 767px) {
      padding: 6px 10px;
      font-size: 14px;
    }

    i {
      flex-shrink: 0;
      font-size: 18px;
      line-height: 1.1;
    }

    &.is-error {
      border-color: #fecaca;
      background: #fef2f2;
      color: #991b1b;

      i {
        color: #dc2626;
      }
    }

    &.is-warning {
      border-color: #fde68a;
      background: #fffbeb;
      color: #92400e;

      i {
        color: #d97706;
      }
    }
  }

  .vin-verification-block,
  .bulk-panel {
    margin: 12px 16px 0;

    @media (max-width: 767px) {
      margin: 8px 8px 0;
    }
  }

  /* ---------- Contenido ---------- */
  .order-content {
    padding: 16px;
    overflow-x: hidden;
    overflow-x: clip;

    @media (max-width: 767px) {
      padding: 8px 8px 80px;
    }
  }

  .mobile-section-tabs {
    display: flex;
    gap: 4px;
    padding: 4px;
    background: white;
    border: 1px solid $ob-border;
    border-radius: 12px;

    // Compactas: debajo viene la tarjeta con su propio titulo
    button {
      display: inline-flex;
      flex: 1;
      align-items: center;
      justify-content: center;
      gap: 6px;
      height: 34px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      color: #4b5563;

      i {
        font-size: 16px;
      }

      &.is-active {
        background: $link-color-1;
        color: white;
      }
    }

    @media (min-width: 768px) {
      display: none;
    }
  }

  .mobile-tab-content {
    animation: ob-fade-in 0.2s ease-out;
  }

  // Tarjetas: los subcomponentes usan .box / .box-header globales (gris con
  // texto blanco); dentro del modal se ven como tarjetas blancas
  .order-view .box {
    background: white;
    border: 1px solid $ob-border;
    border-radius: 12px;
    box-shadow: $ob-card-shadow;
  }

  .order-view .box-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 48px;
    padding: 8px 14px;
    background: $ob-card-header-bg;
    border-bottom: 1px solid $ob-border;
    border-radius: 12px 12px 0 0;
    font-size: 18px;
    font-weight: 600;
    color: $ob-text;

    .title {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: $ob-text;
    }

    @media (max-width: 767px) {
      min-height: 40px;
      padding: 6px 10px;
      font-size: 16px;

      .title {
        font-size: 16px;
      }
    }
  }

  .order-view .box-content-block,
  .order-view .box > .content {
    padding: 12px 14px;

    @media (max-width: 767px) {
      padding: 8px 10px;
    }
  }

  .order-view {
    table.first-dark {
      width: 100%;
      border-collapse: collapse;

      td {
        padding: 7px 4px;
        border-bottom: 1px solid #f3f4f6;
        vertical-align: middle;
        font-size: 16px;
        color: $ob-text;

        @media (max-width: 767px) {
          padding: 5px 4px;
        }
      }

      tbody td:first-child {
        width: 40%;
        padding-right: 12px;
        font-size: 13px;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: $ob-muted;
      }

      tr:last-child td {
        border-bottom: none;
      }
    }

    input.bordered,
    select.bordered,
    textarea.bordered {
      min-height: 36px;
      margin-bottom: 0;
      padding: 6px 10px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      background-color: white;
      box-shadow: none;
      font-size: 16px;
      color: $ob-text;
      transition: border-color 0.15s, box-shadow 0.15s;

      &:focus {
        outline: none;
        border-color: $link-color-1;
        box-shadow: 0 0 0 3px rgba(18, 110, 106, 0.12);
      }

      // Gana por especificidad a la regla global de App.scss, asi que repite
      // el mismo gris: #f9fafb era casi blanco y un campo bloqueado no se
      // distinguia de uno editable
      &:disabled {
        background-color: #f3f4f6;
        border-color: $ob-border;
        cursor: not-allowed;
        color: $ob-muted;
        -webkit-text-fill-color: $ob-muted;
      }
    }

    // Tablas de las pestañas (logs, versiones, pagos, envios, devoluciones)
    table.default-table {
      border-radius: 0;
      background: transparent;
      font-size: 16px;

      thead {
        height: auto;
        background: #f9fafb;

        th {
          height: auto;
          padding: 8px 12px;
          border-bottom: 1px solid $ob-border;
          font-size: 16px;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-align: left;
          text-transform: uppercase;
          color: $ob-muted;

          span {
            position: static;
          }
        }
      }

      tr {
        height: auto;
        font-size: 16px;
        line-height: 1.4;
        color: #374151;
        cursor: default;
      }

      td {
        height: auto !important;
        padding: 8px 12px;
        border-bottom: 1px solid #f3f4f6;
        vertical-align: middle;
      }

      tbody tr:nth-child(even) {
        background-color: transparent;
      }

      tbody tr:hover,
      > tr:hover {
        background-color: #f9fafb;
        color: #374151;

        a {
          color: $link-color-1;
        }
      }
    }
  }

  /* ---------- Pestañas inferiores ---------- */
  .order-tabs {
    min-width: 0;
    background: white;
    border: 1px solid $ob-border;
    border-radius: 12px;
    box-shadow: $ob-shadow;
  }

  .order-tabs-bar {
    display: flex;
    gap: 2px;
    padding: 0 8px;
    border-bottom: 1px solid $ob-border;
    overflow-x: auto;
    // Con overflow-x: auto el eje y tambien hace scroll: el subrayado de la
    // pestana activa (bottom: -1px) lo desbordaba 1px y aparecia un scroll
    // vertical en movil
    overflow-y: hidden;
    scrollbar-width: thin;
  }

  .order-tab {
    position: relative;
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    gap: 6px;
    height: 46px;
    padding: 0 12px;
    font-size: 16px;
    font-weight: 500;
    color: $ob-muted;
    white-space: nowrap;
    transition: color 0.15s;

    i {
      font-size: 18px;
    }

    @media (max-width: 767px) {
      height: 40px;
      padding: 0 10px;
      font-size: 15px;
    }

    &::after {
      content: "";
      position: absolute;
      right: 8px;
      bottom: 0;
      left: 8px;
      height: 2px;
      border-radius: 2px;
      background: transparent;
    }

    &:hover {
      color: $ob-text;
    }

    &:focus-visible {
      outline: 2px solid $link-color-1;
      outline-offset: -4px;
      border-radius: 8px;
    }

    .tab-count {
      min-width: 24px;
      height: 24px;
      padding: 0 7px;
      border-radius: 999px;
      background: #f3f4f6;
      font-size: 16px;
      font-weight: 600;
      line-height: 24px;
      text-align: center;
      color: #4b5563;
    }

    &.is-active {
      font-weight: 600;
      color: $link-color-1;

      &::after {
        background: $link-color-1;
      }

      .tab-count {
        background: $ob-accent-soft;
        color: $link-color-1;
      }
    }
  }

  // La pestaña ya es la tarjeta: el contenido va sin borde propio y su
  // cabecera queda como subtitulo
  .order-tab-panel {
    min-height: 120px;

    > .box {
      border: none;
      border-radius: 0 0 12px 12px;
      box-shadow: none;
    }

    > .box > .box-header {
      min-height: 0;
      padding: 12px 14px 0;
      background: transparent;
      border-bottom: none;
      font-size: 16px;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: $ob-muted;
    }
  }

  // En 2K+ las pestañas comparten filas con general/dirección/totales y con
  // productos: no deben estirarlas (13 logs las harían gigantes). height: 0
  // las saca del cálculo y min-height: 100% las ajusta a ese alto;
  // el contenido que sobre scrollea dentro del panel.
  @media (min-width: 2000px) {
    .order-tabs {
      display: flex;
      flex-direction: column;
      height: 0;
      min-height: 100%;
    }

    // En la columna lateral no caben en una línea: se acomodan en varias
    // filas en vez de mostrar scroll horizontal
    .order-tabs-bar {
      flex-wrap: wrap;
      flex-shrink: 0;
      row-gap: 0;
      padding: 4px 8px 0;
      overflow-x: visible;
    }

    .order-tab {
      height: 40px;
      padding: 0 10px;
    }

    .order-tab-panel {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
    }

    // Pagos: 8 columnas no caben en la columna lateral. Cada pago pasa a ser
    // una tarjeta con etiqueta/valor (las etiquetas salen de data-label).
    .payments {
      .table-full {
        overflow: visible;
      }

      table.default-table {
        display: block;

        thead {
          display: none;
        }

        tbody {
          display: block;
        }

        tr {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px 16px;
          margin-bottom: 10px;
          padding: 10px 12px;
          border: 1px solid $ob-border;
          border-radius: 10px;
        }

        td {
          display: block;
          padding: 0;
          border-bottom: none;
          text-align: left !important;
          overflow-wrap: anywhere;

          &.is-empty {
            display: none;
          }

          &::before {
            content: attr(data-label);
            display: block;
            font-size: 13px;
            font-weight: 600;
            letter-spacing: 0.04em;
            text-transform: uppercase;
            color: $ob-muted;
          }

          &.bg-red-400 {
            padding: 4px 8px;
            border-radius: 6px;

            &::before {
              color: inherit;
            }
          }
        }

        .payment-transaction {
          grid-column: 1 / -1;
        }

        .payment-actions {
          display: flex;
          grid-column: 1 / -1;
          justify-content: flex-end;
          align-items: center;

          &::before {
            content: none;
          }
        }
      }
    }
  }

  /* ---------- Estados ---------- */
  .updating-overlay {
    position: absolute;
    inset: 0;
    z-index: 40;
    background: $ob-canvas-scrim;
    cursor: progress;
  }

  .updating-indicator {
    position: fixed;
    top: 50%;
    left: 50%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 18px;
    background: white;
    border: 1px solid $ob-border;
    border-radius: 999px;
    box-shadow: 0 8px 24px rgba(16, 24, 40, 0.14);
    transform: translate(-50%, -50%);
    font-size: 16px;
    font-weight: 500;
    color: #374151;
  }

  .order-loading {
    min-height: 60vh;
  }

  .order-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    max-width: 960px;
    margin: 48px auto;
    padding: 32px 24px;
    background: white;
    border: 1px solid $ob-border;
    border-radius: 16px;
    text-align: center;

    @media (max-width: 767px) {
      margin: 16px 8px;
      padding: 24px 16px;
    }

    .state-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: #f3f4f6;
      font-size: 30px;
      color: $ob-muted;

      &.is-error {
        background: #fef2f2;
        color: #dc2626;
      }
    }

    .state-title {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      color: $ob-text;
    }

    .state-text {
      max-width: 540px;
      margin: 0;
      font-size: 16px;
      color: $ob-muted;

      strong {
        color: $ob-text;
      }
    }

    .state-facts {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 6px 16px;
      font-size: 16px;
      color: $ob-muted;

      strong {
        color: $ob-text;
      }
    }

    .state-items {
      width: 100%;
      margin-top: 12px;
      overflow-x: auto;
      border: 1px solid $ob-border;
      border-radius: 10px;
      text-align: left;

      table {
        width: 100%;
        border-collapse: collapse;
        font-size: 16px;
      }

      th {
        padding: 8px 12px;
        background: #f9fafb;
        font-size: 16px;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: $ob-muted;
      }

      td {
        padding: 8px 12px;
        border-top: 1px solid #f3f4f6;
        color: #374151;
      }
    }
  }
}

@keyframes ob-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Movil: evitar desbordes horizontales dentro del modal. Antes estas reglas no
   estaban acotadas y cambiaban tablas, botones e iconos de toda la app. */
@media (max-width: 767px) {
  body,
  html {
    max-width: 100vw !important;
    overflow-x: hidden !important;
  }

  .order-modal {
    > .modal-content {
      width: 100vw !important;
      max-width: 100vw !important;
      margin: 0 !important;
      box-sizing: border-box !important;
    }

    .order-content,
    .order-content > * {
      width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
      overflow-x: hidden !important;
    }

    .box {
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      box-sizing: border-box !important;
    }

    .box table {
      width: 100% !important;
      max-width: 100% !important;
      table-layout: fixed !important;
    }

    .box td,
    .box th {
      word-break: break-word !important;
      overflow-wrap: break-word !important;
    }

    input,
    select,
    textarea {
      max-width: 100% !important;
      box-sizing: border-box !important;
    }

    .box-content-block {
      width: 100% !important;
      max-width: 100% !important;
      overflow-x: hidden !important;
    }

    .info-box {
      width: 100% !important;
      max-width: 100% !important;
      overflow-x: auto !important;
    }

    // La pestana vertical de nkrsync (40px de ancho) tapaba el borde derecho
    // de las tarjetas. !important: tailwind.config tiene important: true (le
    // gana a w-10) y el padding va inline
    #nkrsync-button {
      width: 22px !important;
      padding: 6px 0 !important;
      font-size: 11px;
      opacity: 0.85;
    }

    // Editando productos, la lista ocupa toda la pantalla: la cabecera fija
    // de la orden se comia ~75px y nkrsync tapaba los botones de las
    // tarjetas. Se sale con Descartar/Guardar de la barra inferior
    &.is-editing-products {
      > .modal-content > .header {
        display: none;
      }

      // !important: el boton lleva la clase "block" de tailwind (important)
      #nkrsync-button {
        display: none !important;
      }
    }
  }
}

@media (max-width: 390px) {
  .order-modal {
    .order-content * {
      max-width: 100% !important;
      box-sizing: border-box !important;
    }

    .order-content td,
    .order-content th {
      white-space: normal !important;
    }
  }
}

/* Pestañas grises genericas: las usan NewPaymentModal, EditCategoryModal, EditBrandModal y EditCoupon */
.bar {
  @apply flex flex-row w-full overflow-x-auto mb-2;
  background-color: gray;

  @media (max-width: 767px) {
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;

    &::-webkit-scrollbar {
      height: 4px;
    }

    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.1);
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.3);
      border-radius: 2px;
    }
  }
}

.bar .tablinks {
  @apply p-2 text-white hover:bg-gray-400;

  @media (max-width: 767px) {
    flex-shrink: 0;
    white-space: nowrap;
    min-width: max-content;
    padding: 8px 12px;
    font-size: 14px;
  }

  @media (max-width: 390px) {
    padding: 6px 10px;
    font-size: 13px;
  }
}

.bar .tablinks.active {
  @apply bg-black;
}

.vin-verification-block {
  margin: 8px 0;
  border: 1px solid #6c63ff;
  border-radius: 8px;
  overflow: hidden;
  background: #0f0f1a;
  color: #e0e0e0;
}

.vin-verification-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: #1a1a2e;
  border-bottom: 1px solid #2a2a3e;
  flex-wrap: wrap;
}

.vin-verification-title {
  font-size: 16px;
  font-weight: 700;
  color: #a78bfa;
  white-space: nowrap;
}

.vin-decoded {
  font-size: 16px;
  color: #888;
  flex: 1;
}

.vin-close-btn {
  background: none;
  border: none;
  color: #666;
  font-size: 20px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  margin-left: auto;

  &:hover {
    color: #e0e0e0;
  }
}

.vin-results {
  display: flex;
  flex-direction: column;
}

.vin-result-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid #1e1e2e;
  font-size: 16px;

  &:last-child {
    border-bottom: none;
  }

  &--ok {
    border-left: 3px solid #22c55e;
  }
  &--fail {
    border-left: 3px solid #ef4444;
  }
  &--unknown {
    border-left: 3px solid #f59e0b;
  }
}

.vin-result-badge {
  font-weight: 700;
  font-size: 16px;
  flex-shrink: 0;
  width: 20px;
  text-align: center;

  .vin-result--ok & {
    color: #22c55e;
  }
  .vin-result--fail & {
    color: #ef4444;
  }
  .vin-result--unknown & {
    color: #f59e0b;
  }
}

.vin-result-code {
  font-weight: 600;
  color: #d0d0e0;
  white-space: nowrap;
  flex-shrink: 0;
  min-width: 80px;
}

.vin-result-reason {
  color: #aaa;
  line-height: 1.4;
}

.bulk-panel {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin: 12px 0;
  background: #fff;

  .bulk-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    border-bottom: 1px solid #e5e7eb;
    background: #f9fafb;
    border-radius: 8px 8px 0 0;
  }

  .bulk-panel-close {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #e5e7eb;
    font-weight: bold;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
    &:hover {
      background: #d1d5db;
    }
  }

  .bulk-panel-body {
    padding: 16px;
  }

  .bulk-table-wrap {
    max-height: 55vh;
    overflow-y: auto;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    margin-top: 8px;
  }
}
</style>
